import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { formatINR, formatDateIST } from '@/utils/format';
import {
  getFullProductionOrderDetails,
  confirmProductionOrder,
  startProductionOrder,
  continueProduction,
  completeProductionOrderFlow,
  updateProductionOrderStatus,
  getMaterialReservations,
  reserveMaterial,
  releaseMaterial,
  reserveAllMaterials,
  MaterialReservation,
} from '@/api/productionOrder.api';
import { updateWorkOrder, completeWorkOrderApi } from '@/api/workorder.api';
import { createProductionEntry } from '@/api/productionentry.api';
import { getAllOperators } from '@/api/operators.api';
import { getAllShifts } from '@/api/shift.api';
import { getAllWarehouses } from '@/api/warehouse.api';
import POPrintModal from '@/pages/manufacturing/production-orders/components/POPrintModal';
import BOMExplosionModal from '@/pages/manufacturing/bom/components/BOMExplosionModal';
import QualityControlTab from './components/QualityControlTab';
import { StandardCostDetailsModal } from './components/StandardCostDetailsModal';
import { getStageProgress } from '../../../../utils/stageProgress';
import { ActualCostDetailsModal } from '../../costing/variance/components/ActualCostDetailsModal';
import OrderConfirmationSummaryModal from '../components/OrderConfirmationSummaryModal';
import ItemTypeBadge from '../components/ItemTypeBadge';
import {
  getProductionOrderIssueQueue,
  confirmIssueQueueItem,
  confirmStageIssueQueue,
  type IssueQueueItem,
} from '@/api/issueQueue.api';
import { useWorkCenters } from '@/hooks/useWorkCenters';
import ScrapTab from './ScrapTab';

// ─── Status Badge Colors ──────────────────────────────────────────────────────
const STATUS_BADGES: Record<string, { bg: string; text: string; border: string; icon: string }> = {
  DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', icon: 'ri-draft-line' },
  PLANNED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'ri-calendar-check-line' },
  CONFIRMED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'ri-calendar-check-line' },
  IN_PROGRESS: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'ri-loader-4-line' },
  PARTIALLY_DONE: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: 'ri-pie-chart-line' },
  COMPLETED: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'ri-check-line' },
  ON_HOLD: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'ri-pause-circle-line' },
  CANCELLED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'ri-close-circle-line' },
};

// ─── Live Timer Component ──────────────────────────────────────────────────────
function ElapsedTimer({ startDate }: { startDate: string | Date }) {
  const [elapsed, setElapsed] = useState('00:00:00');

  useEffect(() => {
    if (!startDate) return;
    const startMs = new Date(startDate).getTime();

    const updateTimer = () => {
      const nowMs = Date.now();
      const diffSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
      const hours = Math.floor(diffSec / 3600);
      const mins = Math.floor((diffSec % 3600) / 60);
      const secs = diffSec % 60;
      setElapsed(
        `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      );
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [startDate]);

  return (
    <span className="font-mono text-amber-700 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-xs inline-flex items-center gap-1">
      <i className="ri-time-line text-amber-500 animate-spin" /> {elapsed}
    </span>
  );
}

export default function ProductionOrderDetailPage() {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'overview' | 'materials' | 'workorders' | 'shopfloor' | 'qc' | 'scrap' | 'costing' | 'documents'>('overview');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showStdCostModal, setShowStdCostModal] = useState(false);
  const [showActualCostModal, setShowActualCostModal] = useState(false);
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
  const [showGateMatrix, setShowGateMatrix] = useState(false);

  // Modal State for Issue Material
  const [issueMaterialModalOpen, setIssueMaterialModalOpen] = useState(false);
  const [issueItems, setIssueItems] = useState<any[]>([]);
  const [issueOperator, setIssueOperator] = useState('');
  const [issueOperators, setIssueOperators] = useState<any[]>([]);
  const [issueDateTime, setIssueDateTime] = useState('');

  // Material Reservations State
  const [reservations, setReservations] = useState<MaterialReservation[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [showBomExplosionModal, setShowBomExplosionModal] = useState(false);
  const [confirmedSummary, setConfirmedSummary] = useState<any | null>(null);

  // Modal State for Start Production Order (PLANNED → IN_PROGRESS)
  const [startOrderModalOpen, setStartOrderModalOpen] = useState(false);
  const [startOrderForm, setStartOrderForm] = useState({
    actualStartDate: '',
    shiftId: '',
    warehouseId: '',
    supervisorId: '',
    notes: '',
  });
  const [startOrderMasters, setStartOrderMasters] = useState<{ shifts: any[]; warehouses: any[]; supervisors: any[] }>({
    shifts: [],
    warehouses: [],
    supervisors: [],
  });
  const [issueQueueItems, setIssueQueueItems] = useState<IssueQueueItem[]>([]);
  const [materialsViewMode, setMaterialsViewMode] = useState<'stage' | 'flat'>('stage');

  // ── Fetch Full Details ──
  const fetchDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getFullProductionOrderDetails(id);
      if (res.success) {
        setData(res.data);
        fetchQueue();
      } else {
        toast.error(res.message || 'Failed to load production order details');
      }
    } catch (err: any) {
      toast.error(err.message || 'Error fetching production order');
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  const fetchReservations = useCallback(async () => {
    if (!id) return;
    setReservationsLoading(true);
    try {
      const res = await getMaterialReservations(id);
      if (res?.data) {
        setReservations(res.data);
      }
    } catch {
      // quiet fallback
    } finally {
      setReservationsLoading(false);
    }
  }, [id]);

  const fetchQueue = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getProductionOrderIssueQueue(id);
      if (res?.data) {
        setIssueQueueItems(res.data);
      }
    } catch {
      // quiet fallback
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    if (id) {
      fetchReservations();
      fetchQueue();
    }
  }, [id, fetchReservations, fetchQueue]);

  const handleReserveAll = async () => {
    if (!id) return;
    if (data?.order?.status === 'ON_HOLD') {
      toast.error('Cannot reserve materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    try {
      const res = await reserveAllMaterials(id);
      if (res.success) {
        if ((res as any).shortageCount > 0 || (res as any).partialCount > 0) {
          toast.warning(res.message || 'Some items have shortage in warehouse stock ⚠️');
        } else {
          toast.success(res.message || 'Reserved all materials ');
        }
      } else {
        toast.error(res.message || 'Failed to reserve materials ');
      }
      fetchReservations();
      fetchDetails();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reserve materials ');
      fetchReservations();
    }
  };

  const handleReserveOne = async (mr: any) => {
    if (!id) return;
    if (data?.order?.status === 'ON_HOLD') {
      toast.error('Cannot reserve materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    try {
      const res = await reserveMaterial(id, mr.id);
      if (res.success) {
        if ((res as any).caseType === 'C' || (res.data as any)?.status === 'PARTIAL') {
          toast.warning(res.message || `Partially reserved ${mr.itemName} `);
        } else {
          toast.success(res.message || `Reserved ${mr.itemName} `);
        }
      } else {
        toast.error(res.message || `Insufficient stock for ${mr.itemName}`);
      }
      fetchReservations();
      fetchDetails();
    } catch (err: any) {
      toast.error(err?.message || `Insufficient stock for ${mr.itemName}`);
      fetchReservations();
    }
  };

  const handleReleaseOne = async (mr: MaterialReservation) => {
    if (!id) return;
    if (data?.order?.status === 'ON_HOLD') {
      toast.error('Cannot unreserve materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    try {
      const res = await releaseMaterial(id, mr.id);
      toast.info(res.message || `Unreserved ${mr.itemName}. Released stock back to warehouse.`);
      fetchReservations();
      fetchDetails();
    } catch (err: any) {
      toast.error(err?.message || `Failed to unreserve ${mr.itemName}`);
      fetchReservations();
    }
  };

  const handleConfirmStageIssue = async (stageNumber: number, stageName?: string) => {
    if (!id) return;
    if (data?.order?.status === 'ON_HOLD') {
      toast.error('Cannot issue materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    setActionLoading(true);
    try {
      const res = await confirmStageIssueQueue(id, stageNumber);
      if (res.success) {
        toast.success(`Stage ${stageNumber} (${stageName || 'Stage'}) materials confirmed — Stock moved & COA Posted!`);
        await fetchDetails();
        await fetchReservations();
        await fetchQueue();
      } else {
        toast.error(res.message || 'Failed to confirm stage materials');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Error confirming stage issue');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmQueueItem = async (queueId: string, itemName: string, qty: number, unit: string) => {
    if (!id) return;
    if (data?.order?.status === 'ON_HOLD') {
      toast.error('Cannot issue materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    setActionLoading(true);
    try {
      const res = await confirmIssueQueueItem(id, queueId);
      if (res.success) {
        toast.success(`Issued: ${itemName} (${qty} ${unit}) — Stock moved & COA Posted!`);
        await fetchDetails();
        await fetchReservations();
        await fetchQueue();
      } else {
        toast.error(res.message || 'Failed to issue material');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Error confirming issue');
    } finally {
      setActionLoading(false);
    }
  };

  const displayReservations = useMemo(() => {
    if (data?.order?.status === 'DRAFT') {
      return [];
    }
    const mats = data?.materials || [];
    const matMap: Record<string, any> = {};
    mats.forEach((m: any) => {
      const key = m.itemId || m.id;
      matMap[key] = m;
    });

    if (reservations.length > 0) {
      return reservations.map((r: any) => {
        const mat = matMap[r.itemId] || {};
        return {
          ...r,
          bomItemId: mat.id || null,
          bom_item_id: mat.id || null,
          unitPrice: Number(r.unitPrice ?? mat.unitPrice ?? 0),
          itemType: r.itemType || mat.itemType || 'RAW_MATERIAL',
          stockAvailable: r.stockAvailable !== undefined ? Number(r.stockAvailable) : Number(mat.stockAvailable ?? 0),
        };
      });
    }

    return mats.map((m: any, idx: number) => {
      const req = Number(m.requiredQty || m.required_qty || 0);
      const issued = Number(m.issuedQty || m.issued_qty || 0);
      const isReserved = issued >= req && req > 0;
      const isPartial = issued > 0 && issued < req;
      const status: 'RESERVED' | 'PARTIAL' | 'UNRESERVED' = isReserved ? 'RESERVED' : isPartial ? 'PARTIAL' : 'UNRESERVED';
      return {
        id: m.id || `mat-${idx}`,
        bomItemId: m.id || null,
        bom_item_id: m.id || null,
        productionOrderId: id || '',
        itemId: m.itemId || m.item_id || '',
        itemName: m.itemName || m.item_name || null,
        itemCode: m.itemCode || m.item_code || `ITM-${String(idx + 1).padStart(4, '0')}`,
        requiredQty: req,
        reservedQty: 0,
        unitPrice: Number(m.unitPrice || 0),
        itemType: m.itemType || m.item_type || null,
        stockAvailable: Number(m.stockAvailable ?? m.stock_available ?? 0),
        unit: m.unit || m.uom || null,
        status,
      } as any;
    });
  }, [reservations, data?.materials, id]);

  const allItemsReserved = useMemo(() => {
    if (displayReservations.length === 0) return false;
    return displayReservations.every((mr: any) => {
      const resQty = Number(mr.reservedQty || 0);
      const issQty = Number(mr.issuedQty || 0);
      const reqQty = Number(mr.requiredQty || 0);
      return mr.status === 'RESERVED' || mr.status === 'PARTIAL' || mr.status === 'ISSUED' || resQty > 0 || issQty >= reqQty;
    });
  }, [displayReservations]);

  const allMaterialsIssued = useMemo(() => {
    if (displayReservations.length === 0) return false;
    return displayReservations.every((mr: any) => {
      const reqQty = Number(mr.requiredQty || 0);
      const issQty = Number(mr.issuedQty || 0);
      return mr.status === 'ISSUED' || (issQty >= reqQty && reqQty > 0);
    });
  }, [displayReservations]);

  const allModalItemsIssued = useMemo(() => {
    if (issueItems.length === 0) return allMaterialsIssued;
    return issueItems.every((it: any) => {
      const req = Number(it.requiredQty || 0);
      const issued = Number(it.alreadyIssued || 0);
      return issued >= req && req > 0;
    });
  }, [issueItems, allMaterialsIssued]);

  const stageGroupedMaterials = useMemo(() => {
    const rawOrder = data?.order;
    if (!rawOrder) return null;
    const order = rawOrder.status === 'CONFIRMED' ? { ...rawOrder, status: 'PLANNED' } : rawOrder;
    const routingId = order?.routing_id ?? order?.routingId ?? null;
    const hasRouting = routingId !== null && routingId !== undefined && routingId !== '';
    if (!hasRouting) return null;

    const rStages = Array.isArray(order?.routing_stages)
      ? order.routing_stages
      : typeof order?.routing_stages === 'string'
        ? (() => { try { return JSON.parse(order.routing_stages); } catch { return []; } })()
        : [];

    if (rStages.length === 0) return null;

    const workOrders = data?.workOrders || [];
    const isCompletedOrder = order.status === 'COMPLETED';

    const stageMap: Record<number, {
      stageNumber: number;
      stageName: string;
      workOrder: any | null;
      woNumber: string | null;
      isLocked: boolean;
      isCompleted: boolean;
      isActive: boolean;
      items: any[];
    }> = {};

    rStages.forEach((st: any, idx: number) => {
      const seq = Number(st.sequence || st.sequence_no || st.stageNumber || st.stage_number || idx + 1);
      const name = st.operationName || st.stageName || st.stage_name || st.name || `Stage ${seq}`;

      const wo = Array.isArray(workOrders) ? workOrders.find((w: any) => Number(w.stage_number) === seq) : null;
      const prevStages = Array.isArray(workOrders) ? workOrders.filter((w: any) => Number(w.stage_number || 0) < seq) : [];
      const uncompletedPrev = prevStages.some((w: any) => w.status !== 'COMPLETED');
      const isCompleted = wo?.status === 'COMPLETED' || isCompletedOrder;
      const isLocked = Boolean(wo ? (wo.is_locked || uncompletedPrev) : (seq > 1));
      const isActive = wo?.status === 'IN_PROGRESS' || (!isLocked && !isCompleted);

      stageMap[seq] = {
        stageNumber: seq,
        stageName: name,
        workOrder: wo,
        woNumber: wo?.wo_number || null,
        isLocked,
        isCompleted,
        isActive,
        items: [],
      };
    });

    const mappedMaterialItemIdsAcrossAllStages = new Set<string>();

    // For each stage, populate its mapped BOM materials
    rStages.forEach((st: any, idx: number) => {
      const seq = Number(st.sequence || st.sequence_no || st.sequenceNumber || st.stageNumber || st.stage_number || idx + 1);
      const rawCompIds = st.bom_component_ids || st.bomComponentIds || st.materials || [];
      const stageCompIdSet = new Set<string>();
      if (Array.isArray(rawCompIds)) {
        rawCompIds.forEach((c: any) => {
          const cid = typeof c === 'object' ? (c.id || c.itemId || c.bom_component_id || c.item_id) : c;
          if (cid) {
            stageCompIdSet.add(String(cid));
            mappedMaterialItemIdsAcrossAllStages.add(String(cid));
          }
        });
      }

      const stage = stageMap[seq];
      if (!stage) return;

      const addedItemIds = new Set<string>();

      displayReservations.forEach((mr: any) => {
        const mrId = String(mr.id || '');
        const mrItemId = String(mr.itemId || mr.item_id || '');
        const mrBomItemId = String(mr.bomItemId || mr.bom_item_id || '');

        const isMappedToThisStage =
          stageCompIdSet.has(mrId) ||
          stageCompIdSet.has(mrItemId) ||
          stageCompIdSet.has(mrBomItemId);

        if (isMappedToThisStage && !addedItemIds.has(mrItemId)) {
          addedItemIds.add(mrItemId);

          // Find queue item for this material and this specific stage
          const qItem = issueQueueItems.find(
            (q) =>
              (String(q.item_id) === mrItemId || String(q.bom_component_id) === mrBomItemId) &&
              Number(q.stage_number) === seq
          );

          stage.items.push({
            ...mr,
            queueItem: qItem || null,
            queueId: qItem?.id || null,
            queueStatus: qItem?.status || (stage.isCompleted ? 'ISSUED' : null),
            queueIssuedQty: qItem ? Number(qItem.issued_qty || 0) : (stage.isCompleted ? Number(mr.requiredQty || 0) : 0),
          });
        }
      });
    });

    // General materials = materials not mapped to ANY stage (Rule 2)
    const generalMaterials: any[] = [];
    displayReservations.forEach((mr: any) => {
      const mrId = String(mr.id || '');
      const mrItemId = String(mr.itemId || mr.item_id || '');
      const mrBomItemId = String(mr.bomItemId || mr.bom_item_id || '');

      const isMappedSomewhere =
        mappedMaterialItemIdsAcrossAllStages.has(mrId) ||
        mappedMaterialItemIdsAcrossAllStages.has(mrItemId) ||
        mappedMaterialItemIdsAcrossAllStages.has(mrBomItemId);

      if (!isMappedSomewhere) {
        const qItem = issueQueueItems.find(
          (q) => String(q.item_id) === mrItemId || String(q.bom_component_id) === mrBomItemId
        );
        generalMaterials.push({
          ...mr,
          queueItem: qItem || null,
          queueId: qItem?.id || null,
          queueStatus: qItem?.status || null,
          queueIssuedQty: qItem ? Number(qItem.issued_qty || 0) : 0,
        });
      }
    });

    return {
      stages: Object.values(stageMap).sort((a, b) => a.stageNumber - b.stageNumber),
      generalMaterials,
    };
  }, [data, displayReservations, issueQueueItems]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-3 text-slate-400">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#4f46e5]" />
          <p className="text-sm font-medium">Loading Production Order Details…</p>
        </div>
      </AppLayout>
    );
  }

  if (!data?.order) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-3">
          <i className="ri-error-warning-line text-red-400 text-4xl" />
          <p className="text-slate-600 font-semibold">Production Order Not Found</p>
          <button
            onClick={() => navigate('/manufacturing/production-orders')}
            className="mt-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
          >
            Back to Orders
          </button>
        </div>
      </AppLayout>
    );
  }

  const { order: rawOrder, materials, workOrders, shopFloorEntries, costing, materialCapacity } = data;
  // CONFIRMED is the legacy name of PLANNED — treat both as PLANNED everywhere on this page
  const order = rawOrder.status === 'CONFIRMED' ? { ...rawOrder, status: 'PLANNED' } : rawOrder;
  const isPlannedOrder = order.status === 'PLANNED';
  const isCompletedOrder = order.status === 'COMPLETED';
  const isOnHold = order.status === 'ON_HOLD';
  const statusConfig = STATUS_BADGES[order.status] ?? STATUS_BADGES.DRAFT;
  const plannedQty = Number(order.planned_qty ?? order.plannedQty) || 0;
  const orderUnit = order.item_unit || order.unit || '';
  const completedQty = Number(order.completed_qty ?? order.completedQty) || (isCompletedOrder ? plannedQty : 0);
  const rejectedQty = Number(order.rejected_qty ?? order.rejectedQty) || 0;
  const pendingQty = isCompletedOrder ? 0 : Math.max(0, plannedQty - completedQty);

  const routingId = order.routing_id ?? order.routingId ?? null;
  const hasRouting = routingId !== null && routingId !== undefined && routingId !== '';
  const { total: totalStages, completed: completedStages } = getStageProgress(workOrders);
  const allWosCompleted = totalStages > 0 && completedStages === totalStages;

  let progressPct = 0;
  let progressLabel = '';
  let progressIndicator = '';

  if (order.status === 'DRAFT') {
    progressPct = 0;
    progressLabel = 'DRAFT';
    progressIndicator = 'Awaiting order confirmation · No shop floor work started';
  } else if (hasRouting) {
    progressPct = isCompletedOrder
      ? 100
      : totalStages > 0
        ? Math.round((completedStages / totalStages) * 100)
        : 0;
    const displayStages = isCompletedOrder && totalStages > 0 ? totalStages : completedStages;
    progressLabel = `Stage ${displayStages} / ${totalStages}`;
    progressIndicator = `${displayStages} of ${totalStages} stages completed`;
  } else {
    progressPct = isCompletedOrder
      ? 100
      : plannedQty > 0
        ? Math.min(100, Math.round((completedQty / plannedQty) * 100))
        : 0;
    progressLabel = `${completedQty} / ${plannedQty} units`;
    progressIndicator = `${completedQty} of ${plannedQty} units produced`;
  }

  const progressBarColor =
    progressPct === 0
      ? 'bg-slate-300'
      : progressPct >= 100
        ? 'bg-emerald-500'
        : 'bg-blue-600';

  // Check if any Work Order has QC_FAILED
  const qcFailedWo = workOrders.find((w: any) => w.status === 'QC_FAILED');

  // ─── GATE SYSTEM METRICS & VALIDATIONS ───────────────────────────────────────
  const totalMaterialsRequired = Array.isArray(materials)
    ? materials.reduce((s: number, m: any) => s + Number(m.requiredQty || m.required_qty || 0), 0)
    : 0;
  const totalMaterialsIssued = Array.isArray(materials)
    ? materials.reduce((s: number, m: any) => s + Number(m.issuedQty || m.issued_qty || 0), 0)
    : 0;
  const hasIssuedAnyMaterials =
    totalMaterialsIssued > 0 ||
    isCompletedOrder ||
    (order.status === 'IN_PROGRESS' &&
      Array.isArray(shopFloorEntries) &&
      shopFloorEntries.some(
        (e: any) => e.stage_name === 'Material Issue' || e.entry_type === 'MATERIAL_ISSUE'
      ));

  const hasInProgressWo = Array.isArray(workOrders) && workOrders.some((w: any) => w.status === 'IN_PROGRESS');
  const hasPendingWo = Array.isArray(workOrders) && workOrders.some((w: any) => w.status === 'PENDING');
  const hasPendingQc = Array.isArray(workOrders) && workOrders.some((w: any) => w.status === 'QC_PENDING' || w.status === 'COMPLETED_QC_PENDING');
  const qcPendingWo = Array.isArray(workOrders) ? workOrders.find((w: any) => w.status === 'QC_PENDING' || w.status === 'COMPLETED_QC_PENDING') : null;
  const inProgressWo = Array.isArray(workOrders) ? workOrders.find((w: any) => w.status === 'IN_PROGRESS') : null;
  const pendingWo = Array.isArray(workOrders) ? workOrders.find((w: any) => w.status === 'PENDING') : null;

  // Strict Mode: Check if any Work Order with QC enabled has not PASSED QC
  const routingStages = Array.isArray(order?.routing_stages)
    ? order.routing_stages
    : typeof order?.routing_stages === 'string'
      ? (() => { try { return JSON.parse(order.routing_stages); } catch { return []; } })()
      : [];


  const unpassedQcWo = Array.isArray(workOrders)
    ? workOrders.find((w: any) => {
      const matchedRoutingStage = routingStages.find((st: any, idx: number) =>
        (st.sequence || st.sequence_no || st.sequenceNumber || st.stage_number || st.stageNumber || idx + 1) === Number(w.stage_number) ||
        (st.operationName && w.stage_name && st.operationName.toLowerCase().trim() === w.stage_name.toLowerCase().trim())
      );
      const stageRequiresQc = Boolean(
        matchedRoutingStage?.qcRequired ??
        matchedRoutingStage?.qc_required ??
        matchedRoutingStage?.qc_enabled ??
        matchedRoutingStage?.requires_qc
      );
      const isQcEnabled = stageRequiresQc || Boolean(w.qc_inspection_id) || Boolean(w.qc_result);
      return isQcEnabled && w.qc_result !== 'PASS';
    })
    : null;

  // Gate 5 Completion Check — Strict Mode (All Work Orders COMPLETED & QC PASSED if QC enabled)
  const isGate5Ready =
    allWosCompleted &&
    !unpassedQcWo &&
    !hasPendingQc &&
    !qcFailedWo &&
    !hasInProgressWo &&
    !hasPendingWo &&
    (completedQty > 0 || isCompletedOrder) &&
    order.status === 'IN_PROGRESS';

  let gate5BlockReason = '';
  if (order.status === 'DRAFT') {
    gate5BlockReason = 'Please confirm order first (Gate 2)';
  } else if (isPlannedOrder) {
    gate5BlockReason = 'Production order not started yet';
  } else if (order.status === 'IN_PROGRESS' && !hasIssuedAnyMaterials) {
    gate5BlockReason = 'Materials not yet issued (Gate 3)';
  } else if (hasPendingQc) {
    gate5BlockReason = `QC inspection pending for ${qcPendingWo?.stage_name || 'active stage'}`;
  } else if (qcFailedWo) {
    gate5BlockReason = `Stage ${qcFailedWo.stage_number} failed QC — rework pending`;
  } else if (hasInProgressWo) {
    gate5BlockReason = `Work Order ${inProgressWo?.wo_number || ''} still in progress`;
  } else if (hasPendingWo) {
    gate5BlockReason = `Work Order ${pendingWo?.wo_number || ''} not yet started`;
  } else if (!allWosCompleted && totalStages > 0) {
    gate5BlockReason = `Strict Mode: All stages must be COMPLETED (${completedStages}/${totalStages} completed)`;
  } else if (unpassedQcWo) {
    gate5BlockReason = `Strict Mode: Stage ${unpassedQcWo.stage_number || unpassedQcWo.stage_name} (${unpassedQcWo.wo_number}) requires QC PASS before order completion (Traceability)`;
  } else if (order.status !== 'IN_PROGRESS' && !isCompletedOrder) {
    gate5BlockReason = 'Order must be IN_PROGRESS';
  }

  // ── Handlers ──
  const handleConfirmOrder = async () => {
    setActionLoading(true);
    try {
      const res: any = await confirmProductionOrder(order.id);
      if (res && res.success) {
        toast.success(`Production Order ${order.po_number} confirmed & materials reserved`);
        setConfirmedSummary(res);
        fetchDetails();
        fetchReservations();
      } else {
        toast.error(res?.message || 'Failed to confirm order');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error confirming order');
    } finally {
      setActionLoading(false);
    }
  };

  // Start Production Order: PLANNED → IN_PROGRESS
  const openStartOrderModal = () => {
    const now = new Date();
    setStartOrderForm({
      actualStartDate: new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10),
      shiftId: order.shift_id ? String(order.shift_id) : '',
      warehouseId: order.warehouse_id ? String(order.warehouse_id) : '',
      supervisorId: order.supervisor_id ? String(order.supervisor_id) : '',
      notes: order.notes || '',
    });
    setStartOrderModalOpen(true);
    Promise.all([getAllShifts(), getAllWarehouses(), getAllOperators()])
      .then(([shiftRes, whRes, opRes]) => {
        const active = (row: any) => row.is_active ?? row.isActive ?? true;
        const operators = ((opRes as any)?.data || []).filter(active);
        setStartOrderMasters({
          shifts: ((shiftRes as any)?.data || []).filter(active),
          warehouses: ((whRes as any)?.data || []).filter(active),
          // Supervisors first, then the rest of the active operators
          supervisors: [...operators].sort(
            (a: any, b: any) => Number(b.skill === 'SUPERVISOR') - Number(a.skill === 'SUPERVISOR'),
          ),
        });
      })
      .catch(() => toast.error('Failed to load shifts, warehouses or supervisors'));
  };

  const handleConfirmStartOrder = async () => {
    if (!startOrderForm.actualStartDate) {
      toast.error('Actual Start Date is required');
      return;
    }
    setActionLoading(true);
    try {
      const res = await startProductionOrder(order.id, {
        actual_start_date: startOrderForm.actualStartDate,
        shift_id: startOrderForm.shiftId || null,
        warehouse_id: startOrderForm.warehouseId || null,
        supervisor_id: startOrderForm.supervisorId || null,
        notes: startOrderForm.notes,
      });
      if (res && res.success) {
        toast.success(res.message || `Production Order ${order.po_number} started`);
        setStartOrderModalOpen(false);
        fetchDetails();
        fetchReservations();
      } else {
        toast.error(res?.message || 'Failed to start production order');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error starting production order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinueProduction = async () => {
    setActionLoading(true);
    try {
      const res: any = await continueProduction(order.id);
      if (res?.success) {
        toast.success(res.message || 'Work orders created for the remaining units');
        setShowCompleteConfirmModal(false);
        setActiveTab('workorders');
        fetchDetails();
      } else {
        toast.error(res?.message || 'Could not create work orders for the remaining units');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not create work orders for the remaining units');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmProductionComplete = async () => {
    if (isOnHold) {
      toast.error('Cannot complete order while production is ON HOLD. Please resume order first.');
      return;
    }
    setActionLoading(true);
    try {
      const res: any = await completeProductionOrderFlow(order.id, {
        warehouseId: order.warehouse_id,
      });
      if (res.success) {
        toast.success(res.final_qc
          ? res.message
          : `🎉 Production Order ${order.po_number} Completed! ${res.completed_qty ?? producedToComplete} units moved to Finished Goods${res.batch_created ? ` (Batch: ${res.batch_created})` : ''}.`);
        setShowCompleteConfirmModal(false);
        fetchDetails();
      } else {
        toast.error(res.message);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setActionLoading(true);
    try {
      const res = await updateProductionOrderStatus(order.id, newStatus);
      if (res.success) {
        toast.success(`Status updated to ${newStatus}`);
        fetchDetails();
      } else {
        toast.error(res.message);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm(`Are you sure you want to cancel Production Order ${order.po_number}?`)) {
      return;
    }
    await handleStatusChange('CANCELLED');
  };

  const openStartWorkOrderModal = (wo: any) => {
    if (isOnHold) {
      toast.error('Cannot start Work Order while production order is ON HOLD. Please resume order first.');
      return;
    }
    const stageSeq = Number(wo.stage_number || 0);
    const stageData = stageGroupedMaterials?.stages?.find((s: any) => Number(s.stageNumber) === stageSeq);
    // Assigning sends the stage's materials to the store; starting needs them issued
    if (wo.assigned_at && stageData && stageData.items && stageData.items.length > 0) {
      // A part-issued line lets the work start; the rest is issued when stock arrives
      const allIssued = stageData.items.every((m: any) => ['ISSUED', 'PARTIAL'].includes(m.queueStatus) || (stageData.isCompleted && m.queueStatus !== 'PENDING'));
      if (!allIssued) {
        toast.error(`Cannot start Work Order: Please issue the materials for Stage ${stageSeq} first.`);
        return;
      }
    }
    // Start details are captured on the Work Orders page card
    navigate(`/manufacturing/work-orders?poId=${order.id}&startWo=${wo.id}`);
  };

  const handleConfirmCompleteWorkOrder = async (targetWorkOrder?: any) => {
    const targetWo = targetWorkOrder;
    if (!targetWo) return;
    if (isOnHold) {
      toast.error('Cannot complete Work Order while production order is ON HOLD. Please resume order first.');
      return;
    }
    setActionLoading(true);
    try {
      // Quantities and time come from the production entries logged for this work order
      const res = await completeWorkOrderApi(targetWo.id, {});

      // Case 1: QC required and blocked -> DO NOT OPEN ANY POPUP CARD! DIRECT REDIRECT TO IN-PROCESS QC INSPECTION PAGE!
      if ((res as any).blockedByQc || res.data?.status === 'QC_PENDING' || (res as any).status === 'QC_PENDING') {
        const targetInspId = (res as any).inspection_id || targetWo.qc_inspection_id || '';
        const targetWoNum = (res as any).wo_number || targetWo.wo_number || '';
        const currentUrl = window.location.pathname + window.location.search;
        navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(targetWoNum)}&inspection_id=${targetInspId}&return_url=${encodeURIComponent(currentUrl)}`);
        return;
      } else if (res.success) {
        toast.success(`✅ ${targetWo.stage_name} Completed! Next stage unlocked.`);
        fetchDetails();
      } else {
        toast.error(res.message || 'Failed to complete Work Order');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Error completing Work Order');
      // No production recorded yet: take the user to the Shop Floor entry form for this Work Order
      if (err.response?.data?.code === 'PRODUCTION_ENTRY_REQUIRED') {
        const currentUrl = window.location.pathname + window.location.search;
        navigate(`/manufacturing/shop-floor?woId=${targetWo.id}&return_url=${encodeURIComponent(currentUrl)}`);
      }
    } finally {
      setActionLoading(false);
    }
  };


  // Rework details are captured on the Work Orders page card for the QC-failed stage
  const openReworkForm = (wo: any) => {
    if (isOnHold) {
      toast.error('Cannot create rework while production order is ON HOLD. Please resume order first.');
      return;
    }
    if (!wo?.id) return;
    navigate(`/manufacturing/work-orders?poId=${order.id}&reworkWo=${wo.id}`);
  };

  // Production is recorded on the Shop Floor form, pre-filled for the stage that is running
  const openProductionEntry = () => {
    const runningWo = workOrders.find((w: any) => w.status === 'IN_PROGRESS');
    if (!runningWo) {
      toast.info('Start a work order first, then record its production.');
      return;
    }
    const currentUrl = window.location.pathname + window.location.search;
    navigate(`/manufacturing/shop-floor?woId=${runningWo.id}&return_url=${encodeURIComponent(currentUrl)}`);
  };

  const openIssueMaterialModal = () => {
    if (isOnHold) {
      toast.error('Cannot issue materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    const prepared = materials.map((m: any) => {
      const resv = displayReservations.find((r: any) => r.itemId === m.itemId || r.id === m.id);
      const req = Number(m.requiredQty || resv?.requiredQty || 0);
      const reserved = Number(resv?.reservedQty || 0);
      const issued = Number(m.issuedQty || resv?.issuedQty || 0);
      const remainingToIssue = Math.max(0, req - issued);
      const availableStock = Number(m.stockAvailable ?? m.stock_available ?? resv?.stockAvailable ?? 0);
      const maxIssuableQty = Math.min(remainingToIssue, reserved > 0 ? reserved : availableStock);
      return {
        itemId: m.itemId || m.id,
        itemName: m.itemName,
        itemCode: m.itemCode,
        requiredQty: req,
        reservedQty: reserved,
        alreadyIssued: issued,
        stockAvailable: availableStock,
        maxIssuableQty,
        issueNowQty: maxIssuableQty,
        unit: m.unit || m.uom || resv?.unit,
        unitPrice: Number(m.unitPrice || m.unit_price || 0),
        isBatchTracked: m.isBatchTracked ?? false,
        selectedBatch: availableStock > 0 || reserved > 0 ? 'Standard Stock' : 'Out of Stock',
        availableBatches: [
          { batchNumber: 'Standard Stock', availableQty: availableStock },
        ],
        warehouseName: order.warehouse_name || '—',
      };
    });
    setIssueItems(prepared);
    setIssueOperator('');
    const now = new Date();
    setIssueDateTime(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    setIssueMaterialModalOpen(true);
    // Active operators of the order's warehouse (operators without a warehouse are shared)
    getAllOperators()
      .then((res) => {
        const list = (res.data || []).filter((o: any) => {
          const wh = o.warehouse_id ?? o.warehouseId;
          return (o.is_active ?? o.isActive ?? true) && (!wh || !order.warehouse_id || String(wh) === String(order.warehouse_id));
        });
        setIssueOperators(list);
      })
      .catch(() => toast.error('Failed to load operators'));
  };

  const handleSaveMaterialIssue = async () => {
    if (isOnHold) {
      toast.error('Cannot issue materials while production order is ON HOLD. Please resume order first.');
      return;
    }
    const itemsToIssue = issueItems
      .filter((item) => Number(item.issueNowQty) > 0)
      .map((item) => {
        const isRealBatch = Boolean(
          item.isBatchTracked &&
          item.selectedBatch &&
          item.selectedBatch !== 'Standard Stock' &&
          item.selectedBatch !== 'Out of Stock'
        );
        return {
          item_id: item.itemId,
          itemId: item.itemId,
          quantity: Number(item.issueNowQty),
          qty_issued: Number(item.issueNowQty),
          unit_price: Number(item.unitPrice || 0),
          batch_number: isRealBatch ? item.selectedBatch : undefined,
          batch_id: isRealBatch ? item.selectedBatch : undefined,
          warehouse_id: order.warehouse_id,
        };
      });

    const hasOutOrShortage = issueItems.some(
      (it) =>
        Number(it.stockAvailable || 0) < (Number(it.requiredQty || 0) - Number(it.alreadyIssued || 0)) ||
        Number(it.stockAvailable || 0) <= 0
    );

    if (itemsToIssue.length === 0) {
      toast.warning('No available stock to issue for any items.');
      setIssueMaterialModalOpen(false);
      return;
    }

    const issuedByOperator = issueOperators.find((o: any) => String(o.id) === String(issueOperator));
    if (!issuedByOperator) {
      toast.error('Please select Issued By (Operator)');
      return;
    }

    setActionLoading(true);
    try {
      const res = await createProductionEntry({
        entry_type: 'MATERIAL_ISSUE',
        prd_id: order.id,
        productionOrderId: order.id,
        productionOrderNumber: order.po_number,
        operatorId: issuedByOperator.id,
        operator_name: issuedByOperator.name,
        issue_date_time: issueDateTime,
        warehouseId: order.warehouse_id,
        items: itemsToIssue,
      });

      if (res.success && (res as any).continuation?.workOrders?.length) {
        toast.success(res.message || 'Materials issued. Work orders created for the remaining units.');
        setIssueMaterialModalOpen(false);
        fetchDetails();
        fetchReservations();
      } else if (res.success) {
        if (hasOutOrShortage) {
          toast.success('Materials issued from available stock! Remaining shortages can be checked via BOM Explosion.');
          setIssueMaterialModalOpen(false);
        } else {
          toast.success('Materials issued successfully! Warehouse stock & WIP updated.');
          setIssueMaterialModalOpen(false);
        }
        fetchDetails();
        fetchReservations();
      } else {
        toast.error(res.message || 'Error issuing materials');
      }
    } catch (err: any) {
      toast.error(err.message || 'Error issuing materials');
    } finally {
      setActionLoading(false);
    }
  };


  // Dynamic Standard Costs based on PRD Planned Quantity and BOM
  const plannedOrderQty = Number(order.planned_qty) || 1;
  const isDraftOrder = order.status === 'DRAFT';

  const stdMatCost = isDraftOrder ? 0 : Number(costing?.standard?.materialCost ?? 0);
  const stdLabCost = isDraftOrder ? 0 : Number(costing?.standard?.labourCost ?? 0);
  const stdOvhCost = isDraftOrder ? 0 : Number(costing?.standard?.overheadCost ?? 0);
  const stdTotalCost = isDraftOrder ? 0 : Number(costing?.standard?.totalCost ?? (stdMatCost + stdLabCost + stdOvhCost));
  const stdPerUnit = isDraftOrder ? 0 : (plannedOrderQty > 0 ? Math.round(stdTotalCost / plannedOrderQty) : 0);

  const hasActualProgress = completedQty > 0 || (shopFloorEntries && shopFloorEntries.length > 0) || isCompletedOrder;

  // Actual cost and variance are calculated when the order is completed and stay blank until then
  const savedActuals = isCompletedOrder && Number(order.actual_total_cost) > 0;
  const actMatCost = Number(savedActuals ? order.actual_material_cost : costing?.actual?.materialCost) || 0;
  const actLabCost = Number(savedActuals ? order.actual_labour_cost : costing?.actual?.labourCost) || 0;
  const actOvhCost = Number(savedActuals ? order.actual_overhead_cost : costing?.actual?.overheadCost) || 0;

  const actTotalCost = actMatCost + actLabCost + actOvhCost;
  const actPerUnit = completedQty > 0 ? Math.round(actTotalCost / completedQty) : (isCompletedOrder ? Math.round(actTotalCost / plannedOrderQty) : 0);
  const costVariance = isCompletedOrder ? (actTotalCost - stdTotalCost) : 0;
  const costVariancePct = stdTotalCost > 0 ? Number(((costVariance / stdTotalCost) * 100).toFixed(2)) : 0;
  // Variance as it stands right now (shown on the completion confirmation before the order is closed)
  const closingActualCost = Number(costing?.atCompletion?.totalCost) || 0;
  const liveVariance = closingActualCost - stdTotalCost;
  const liveVariancePct = stdTotalCost > 0 ? Number(((liveVariance / stdTotalCost) * 100).toFixed(2)) : 0;
  const actualPendingLabel = order.status === 'CANCELLED' ? '—' : 'At completion';
  const gradeForVariance = (pct: number) => {
    const abs = Math.abs(pct);
    return abs > 10 ? 'Grade D' : abs > 5 ? 'Grade C' : abs > 2 ? 'Grade B' : 'Grade A';
  };
  const budgetLabel = (amount: number) => (amount > 0 ? 'Over budget' : amount < 0 ? 'Under budget' : 'On budget');
  const costingGrade = `${gradeForVariance(costVariancePct)} (${budgetLabel(costVariance)})`;
  // Units out of the final stage and rejections across all stages, as recorded on the shop floor
  const finalStageWo = [...workOrders].sort((x: any, y: any) => (x.stage_number || 0) - (y.stage_number || 0)).pop();
  const producedToComplete = Number(materialCapacity?.producedQty) || Number(finalStageWo?.completed_qty) || plannedQty;
  // Finished units the issued material can make, and the units still to be produced
  const maxProducible = materialCapacity?.hasMaterials ? Number(materialCapacity.maxProducible) : null;
  const limitingMaterial = materialCapacity?.limiting || null;
  const capacityStages: any[] = Array.isArray(materialCapacity?.stages) ? materialCapacity.stages : [];
  const limitingStage = limitingMaterial
    ? capacityStages.find((st: any) => st.limiting && String(st.limiting.itemId) === String(limitingMaterial.itemId)) || null
    : null;
  const continuation = materialCapacity?.continuation?.eligible ? materialCapacity.continuation : null;
  const shortToComplete = Math.max(0, plannedQty - producedToComplete);
  const maxUnitsByItem: Record<string, any> = {};
  (materials || []).forEach((m: any) => { maxUnitsByItem[String(m.itemId)] = m; });
  const rejectedToComplete = workOrders.reduce((sum: number, w: any) => sum + (Number(w.rejected_qty) || 0), 0);
  const isCancelledOrder = order?.status === 'CANCELLED';
  const costingGradeFinal = isCompletedOrder ? costingGrade : isCancelledOrder ? 'Cancelled' : hasActualProgress ? 'In Progress' : 'Pending';

  return (
    <AppLayout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">

        {/* ── Breadcrumb & Actions Bar ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manufacturing/production-orders')}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors shadow-xs cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">{order.po_number}</h1>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                  <i className={`${statusConfig.icon} text-xs`} />
                  {order.status}
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold uppercase border border-slate-200">
                  {order.type}
                </span>
                {isCompletedOrder && (
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200 flex items-center gap-1">
                    <i className="ri-lock-2-line text-xs" /> Read-Only Mode
                  </span>
                )}
                {/* Output of an item needing Final QC is held until the Final inspection passes */}
                {order.fg_qc_status === 'PENDING' && (
                  <button type="button" onClick={() => navigate('/manufacturing/qc/final')}
                    title="Finished goods go into stock when the Final QC inspection passes"
                    className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200 flex items-center gap-1 cursor-pointer hover:bg-amber-100">
                    <i className="ri-shield-check-line text-xs" /> QC Hold · {Number(order.fg_qc_qty) || 0} units awaiting Final QC
                  </button>
                )}
                {order.fg_qc_status === 'FAILED' && (
                  <span className="px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 text-xs font-semibold border border-rose-200 flex items-center gap-1">
                    <i className="ri-close-circle-line text-xs" /> Final QC failed · {Number(order.fg_qc_qty) || 0} units held (see NCR)
                  </span>
                )}
                {order.fg_qc_status === 'PASSED' && (
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200 flex items-center gap-1">
                    <i className="ri-shield-check-fill text-xs" /> Final QC passed
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Finished Good: <span className="font-semibold text-slate-800">{order.item_name}</span> ({order.item_code}) · Warehouse: <span className="font-medium text-slate-700">{order.warehouse_name || 'Main Warehouse'}</span>
                {order.actual_end_date && ` · Completed: ${formatDateIST(order.actual_end_date)}`}
              </p>
            </div>
          </div>

          {/* Quick Action Buttons */}
          {/* <div className="flex items-center gap-2">
            {order.status === 'CANCELLED' && (
              <span className="h-9 px-3.5 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold flex items-center gap-1.5 shadow-2xs">
                <i className="ri-close-circle-fill text-sm text-rose-500" />
                Order Cancelled (No actions permitted)
              </span>
            )}

            {isOnHold && (
              <>
                <button
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  disabled={actionLoading}
                  className="h-9 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                  title="Resume Production Order"
                >
                  <i className="ri-play-circle-line text-sm" />
                  Resume Order
                </button>
                <button
                  onClick={handleCancelOrder}
                  disabled={actionLoading}
                  className="h-9 px-3.5 rounded-xl border border-rose-200 bg-rose-50/60 hover:bg-rose-100/80 text-rose-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  title="Cancel this Production Order"
                >
                  <i className="ri-close-circle-line text-sm text-rose-500" />
                  Cancel Order
                </button>
              </>
            )}

            {!isCompletedOrder && order.status !== 'CANCELLED' && !isOnHold && (
              <>
                {order.status === 'DRAFT' ? (
                  <>
                    <button
                      onClick={handleConfirmOrder}
                      disabled={actionLoading}
                      className="h-9 px-4 rounded-xl bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                      title="Gate 2: Confirm Order to activate standard costs, reserve materials & generate Work Orders"
                    >
                      <i className="ri-checkbox-circle-line text-sm" />
                      Confirm Order
                    </button>

                    <button
                      onClick={() => navigate(`/manufacturing/production-orders/${order.id}/edit`)}
                      className="h-9 px-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer"
                      title="Edit Production Order (Product, Qty, Dates)"
                    >
                      <i className="ri-edit-line text-sm text-slate-500" />
                      Edit Order
                    </button>

                    <button
                      onClick={handleCancelOrder}
                      disabled={actionLoading}
                      className="h-9 px-3.5 rounded-xl border border-rose-200 bg-rose-50/60 hover:bg-rose-100/80 text-rose-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                      title="Cancel this DRAFT Production Order"
                    >
                      <i className="ri-close-circle-line text-sm text-rose-500" />
                      Cancel Order
                    </button>

                    <button
                      disabled
                      className="h-9 px-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-400 text-xs font-medium cursor-not-allowed flex items-center gap-1.5"
                      title="Cannot complete a DRAFT order. Please confirm order first."
                    >
                      <i className="ri-lock-line text-xs" />
                      Mark Complete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleStatusChange('ON_HOLD')}
                      disabled={actionLoading}
                      className="h-9 px-3.5 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100/80 text-amber-800 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                      title="Put Production Order ON HOLD"
                    >
                      <i className="ri-pause-circle-line text-sm text-amber-600" />
                      Hold Order
                    </button>

                    {isGate5Ready ? (
                      <button
                        onClick={() => setShowCompleteConfirmModal(true)}
                        disabled={actionLoading}
                        className="h-9 px-4 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-all shadow-md cursor-pointer flex items-center gap-1.5 animate-pulse"
                        title="Gate 5: All Work Orders completed & QC passed! Click to complete order."
                      >
                        <i className="ri-check-double-line text-base" />
                        Mark Production Complete
                      </button>
                    ) : (
                      <button
                        disabled
                        className="h-9 px-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-400 text-xs font-medium cursor-not-allowed flex items-center gap-1.5"
                        title={`Gate 5 Locked: ${gate5BlockReason || 'Complete all work orders first'}`}
                      >
                        <i className="ri-lock-line text-xs" />
                        Mark Complete
                      </button>
                    )}

                    {order.status === 'IN_PROGRESS' && !allWosCompleted && (
                      <button
                        onClick={() => setEntryModalOpen(true)}
                        disabled={actionLoading || !hasInProgressWo}
                        className="h-9 px-3.5 rounded-xl bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                        title={hasInProgressWo ? 'Gate 4B: Record partial output or rejections' : 'Start a Work Order first to record production'}
                      >
                        <i className="ri-add-line text-sm" />
                        New Production Entry
                      </button>
                    )}
                  </>
                )}
              </>
            )}

            <button
              onClick={() => setShowPrintModal(true)}
              className="h-9 px-4 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors shadow-xs cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-printer-line text-sm" /> Print Traveler
            </button>
          </div> */}

          {/* Action buttons by status: DRAFT → Confirm · PLANNED → Start Production · IN_PROGRESS → Issue Material / Mark Complete */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isOnHold && (
              <button
                onClick={() => handleStatusChange('IN_PROGRESS')}
                disabled={actionLoading}
                className="h-9 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                title="Resume Production Order"
              >
                <i className="ri-play-circle-line text-sm" />
                Resume Order
              </button>
            )}

            {order.status === 'DRAFT' && (
              <>
                <button
                  onClick={handleConfirmOrder}
                  disabled={actionLoading}
                  className="h-9 px-4 rounded-xl bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                  title="Confirm the order: status becomes PLANNED, work orders are generated"
                >
                  <i className="ri-checkbox-circle-line text-sm" />
                  Confirm
                </button>
                <button
                  onClick={() => navigate(`/manufacturing/production-orders/${order.id}/edit`)}
                  className="h-9 px-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer"
                  title="Edit Production Order (Product, Qty, Dates)"
                >
                  <i className="ri-edit-line text-sm text-slate-500" />
                  Edit Order
                </button>
              </>
            )}

            {isPlannedOrder && (
              <button
                onClick={openStartOrderModal}
                disabled={actionLoading}
                className="h-9 px-4 rounded-xl bg-[#4f46e5] text-white text-xs font-bold hover:bg-indigo-700 transition-colors shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                title="Start production: status becomes IN_PROGRESS"
              >
                <i className="ri-play-line text-sm" />
                Start Production Order
              </button>
            )}

            {order.status === 'IN_PROGRESS' && (
              <>
                {!allMaterialsIssued && (
                  <button
                    onClick={() => {
                      setActiveTab('materials');
                      if (allItemsReserved) openIssueMaterialModal();
                      else toast.info('Reserve the materials first, then issue them to production.');
                    }}
                    disabled={actionLoading}
                    className="h-9 px-3.5 rounded-xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    title="Issue materials to the shop floor"
                  >
                    <i className="ri-share-forward-box-line text-sm" />
                    Issue Material
                  </button>
                )}
                <button
                  onClick={() => handleStatusChange('ON_HOLD')}
                  disabled={actionLoading}
                  className="h-9 px-3.5 rounded-xl border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  title="Put Production Order ON HOLD"
                >
                  <i className="ri-pause-circle-line text-sm" />
                  Hold Order
                </button>
                {hasInProgressWo && (
                  <button
                    onClick={openProductionEntry}
                    disabled={actionLoading}
                    className="h-9 px-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    title="Record produced and rejected quantity for the running work order"
                  >
                    <i className="ri-add-line text-sm" />
                    Production Entry
                  </button>
                )}
                {continuation && (
                  <button
                    onClick={handleContinueProduction}
                    disabled={actionLoading || isOnHold}
                    className="h-9 px-4 rounded-xl border border-indigo-300 bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100 transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                    title={`Issued material covers ${continuation.qty} more unit(s). Creates one work order per stage for them.`}
                  >
                    <i className="ri-play-list-add-line text-base" />
                    Produce Remaining {continuation.qty}
                  </button>
                )}
                {isGate5Ready && (
                  <button
                    onClick={() => setShowCompleteConfirmModal(true)}
                    disabled={actionLoading}
                    className="h-9 px-4 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-all shadow-md cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                    title="All Work Orders completed & QC passed. Click to complete the order."
                  >
                    <i className="ri-check-double-line text-base" />
                    Mark Production Complete
                  </button>
                )}
              </>
            )}

            {(order.status === 'DRAFT' || isPlannedOrder || isOnHold) && (
              <button
                onClick={handleCancelOrder}
                disabled={actionLoading}
                className="h-9 px-3.5 rounded-xl border border-rose-200 bg-rose-50/60 hover:bg-rose-100/80 text-rose-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                title="Cancel this Production Order"
              >
                <i className="ri-close-circle-line text-sm text-rose-500" />
                Cancel Order
              </button>
            )}

            {order.status !== 'DRAFT' && (
              <button
                onClick={() => setShowPrintModal(true)}
                className="h-9 px-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer"
                title="Print the production traveler"
              >
                <i className="ri-printer-line text-sm text-slate-500" />
                Print Traveler
              </button>
            )}
          </div>
        </div>

        {/* ── ON HOLD Notice Banner ── */}
        {/* {isOnHold && (
          <div className="bg-amber-50/90 border border-amber-200/90 rounded-2xl p-4 shadow-xs flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
                <i className="ri-pause-circle-fill text-xl" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-amber-900">Production Order is ON HOLD</h4>
                <p className="text-xs text-amber-700 mt-0.5">
                  All operations (material reservations, material issues, work orders, shop floor logs, and QC inspections) are locked until resumed.
                </p>
              </div>
            </div>
            <button
              onClick={() => handleStatusChange('IN_PROGRESS')}
              disabled={actionLoading}
              className="h-8 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shrink-0"
            >
              <i className="ri-play-line text-sm" />
              Resume Order
            </button>
          </div>
        )} */}

        {/* ── QC Alert Banner ── */}
        {/* Marked completed without the completion flow: nothing was posted */}
        {order.completion_unposted && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2.5 text-xs text-amber-900">
              <i className="ri-error-warning-fill text-amber-600 text-lg shrink-0" />
              <div>
                <p className="font-bold text-sm">Completed, but finished goods and cost were not posted</p>
                <p className="mt-0.5 text-amber-800">
                  This order was only marked completed. No finished goods stock, output batch, actual cost or accounting entry exists for it yet.
                  Posting runs the full completion checks and books {producedToComplete} {orderUnit}.
                </p>
              </div>
            </div>
            <button
              onClick={handleConfirmProductionComplete}
              disabled={actionLoading}
              className="h-9 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-xs cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5 shrink-0"
            >
              {actionLoading ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-check-double-line text-sm" />}
              Post Completion
            </button>
          </div>
        )}

        {qcFailedWo && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5 shadow-xs animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-start justify-between">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 font-bold text-sm text-rose-800">
                  <i className="ri-error-warning-fill text-lg text-rose-600" />
                  QC Inspection Failed — Action Required
                </div>
                <p className="text-xs text-rose-700 font-medium">
                  Stage {qcFailedWo.stage_number} ({qcFailedWo.stage_name}) failed its QC inspection
                  {order.qc_failure?.inspection_number ? ` ${order.qc_failure.inspection_number}` : ''}
                  {order.qc_failure?.checklist_name ? ` — ${order.qc_failure.checklist_name}` : ''}.
                  {order.qc_failure?.failed_parameters && (
                    <> Failed: <strong>{order.qc_failure.failed_parameters}</strong>.</>
                  )}
                </p>
                <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-lg border border-rose-300 font-semibold">
                  {order.qc_failure?.ncr_number
                    ? `NCR ${order.qc_failure.ncr_number} · ${String(order.qc_failure.ncr_status || '').replace(/_/g, ' ')}${order.qc_failure.ncr_severity ? ` · ${order.qc_failure.ncr_severity}` : ''}`
                    : 'No NCR raised yet'}
                  {order.qc_failure?.inspector_name ? ` · Inspector: ${order.qc_failure.inspector_name}` : ''}
                </div>
                <p className="text-[11px] text-rose-700">
                  Create a rework work order for this stage. When the rework is completed and passes QC, this stage is cleared automatically.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/manufacturing/qc/ncr')}
                  className="h-8 px-3 rounded-lg border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 text-xs font-semibold transition-all cursor-pointer"
                >
                  {order.qc_failure?.ncr_number ? 'View NCR' : 'Open NCRs'}
                </button>
                <button
                  onClick={() => openReworkForm(qcFailedWo)}
                  disabled={isOnHold}
                  className="h-8 px-3.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-all shadow-xs cursor-pointer flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isOnHold ? 'Order is ON HOLD' : 'Create Rework'}
                >
                  <i className="ri-restart-line text-sm" /> Rework
                </button>
              </div>
            </div>
          </div>
        )}



        {/* ── Progress Bar Widget ── */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-xs space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-slate-700 flex items-center gap-1.5">
              <i className="ri-flag-2-line text-[#4f46e5]" />
              Production Output Progress:
            </span>
            <span className={`font-bold text-sm ${progressPct === 0 ? 'text-slate-500' : progressPct >= 100 ? 'text-emerald-600' : 'text-blue-600'}`}>
              {progressLabel} ({progressPct}%)
            </span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-200/60">
            <div
              className={`h-full rounded-full transition-all duration-500 ${progressBarColor}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="text-[11px] text-slate-500 font-medium pt-0.5">
            {progressIndicator}
          </div>
        </div>

        {/* ── Tabs Navigation ── */}
        <div className="border-b border-slate-200 flex items-center gap-1 sm:gap-2">
          {[
            { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
            { key: 'materials', label: `Materials (${displayReservations.length})`, icon: 'ri-box-3-line' },
            { key: 'workorders', label: `Work Orders (${workOrders.length})`, icon: 'ri-git-merge-line' },
            { key: 'shopfloor', label: `Shop Floor (${shopFloorEntries.length})`, icon: 'ri-history-line' },
            { key: 'qc', label: 'Quality Control', icon: 'ri-shield-check-line' },
            { key: 'scrap', label: 'Scrap', icon: 'ri-delete-bin-6-line' },
            { key: 'costing', label: 'Costing Summary', icon: 'ri-money-rupee-circle-line' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`h-11 px-4 flex items-center gap-2 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key
                ? 'border-[#4f46e5] text-[#4f46e5] font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
            >
              <i className={`${tab.icon} text-base`} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── TAB 1: OVERVIEW ── */}
        {activeTab === 'overview' && (() => {
          const actualStart = order.actual_start_date ? new Date(order.actual_start_date) : null;
          const actualEnd = order.actual_end_date ? new Date(order.actual_end_date) : null;
          const dateTime = (d: Date | null) =>
            d && !isNaN(d.getTime())
              ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
              : '—';
          // Time on the order so far (or total, once completed)
          const elapsedMs = actualStart ? (actualEnd || new Date()).getTime() - actualStart.getTime() : 0;
          const elapsedDays = Math.floor(elapsedMs / 86400000);
          const elapsedHours = Math.round(((elapsedMs % 86400000) / 3600000) * 10) / 10;
          const plannedEnd = order.planned_end_date ? new Date(order.planned_end_date) : null;
          const isLate =
            !!plannedEnd && !isCompletedOrder && order.status !== 'CANCELLED' && new Date(plannedEnd.getTime() + 86400000) < new Date();
          const stageOf = (status: string) => workOrders.filter((w: any) => w.status === status).length;
          const details: [string, React.ReactNode][] = [
            ['PO Number', order.po_number],
            ['Product', `${order.item_name || '—'}${order.item_code ? ` (${order.item_code})` : ''}`],
            ['Type', order.type === 'MTO' ? 'Make to Order' : order.type === 'MTS' ? 'Make to Stock' : order.type || '—'],
            ['Priority', order.priority || '—'],
            ['BOM', `${order.bom_code || '—'}${order.bom_version_name || order.bom_version ? ` · v${order.bom_version_name || order.bom_version}` : ''}`],
            ['Routing', order.routing_name || (hasRouting ? '—' : 'No routing')],
            ['Warehouse', order.warehouse_name || '—'],
            ['Shift', order.shift_name || '—'],
            ['Supervisor', order.supervisor_name || '—'],
            ['Sales Order', order.sales_order_number || '—'],
          ];
          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Planned', value: plannedQty, tone: 'bg-white border-slate-200 text-slate-800' },
                    { label: 'Completed', value: completedQty, tone: 'bg-emerald-50/70 border-emerald-200 text-emerald-700' },
                    { label: 'Rejected', value: rejectedQty, tone: 'bg-rose-50/70 border-rose-200 text-rose-700' },
                    {
                      label: order.status === 'CANCELLED' ? 'Cancelled' : 'Pending',
                      value: order.status === 'CANCELLED' ? plannedQty : pendingQty,
                      tone: order.status === 'CANCELLED' ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-amber-50/70 border-amber-200 text-amber-700',
                    },
                  ].map((q) => (
                    <div key={q.label} className={`px-4 py-3 rounded-xl border ${q.tone}`}>
                      <span className="block text-xs font-semibold opacity-80">{q.label} Qty</span>
                      <span className="text-2xl font-bold tracking-tight block mt-0.5">
                        {q.value} <span className="text-xs font-medium opacity-70">{orderUnit}</span>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-5">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2.5 mb-3">
                    Order Details
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-3">
                    {details.map(([label, value]) => (
                      <div key={label} className="min-w-0">
                        <span className="text-slate-400 block text-[11px] font-medium">{label}</span>
                        <span className="text-sm font-semibold text-slate-800 block truncate" title={typeof value === 'string' ? value : undefined}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                  {order.notes && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <span className="text-slate-400 block text-[11px] font-medium">Notes</span>
                      <p className="text-xs text-slate-700 whitespace-pre-line">{order.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-5">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2.5 mb-3">Schedule</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Planned Start</span>
                      <span className="font-semibold text-slate-800">{formatDateIST(order.planned_start_date)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Planned Completion</span>
                      <span className={`font-semibold ${isLate ? 'text-rose-600' : 'text-slate-800'}`}>
                        {formatDateIST(order.planned_end_date)}
                        {isLate ? ' · overdue' : ''}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-t border-slate-100 pt-2">
                      <span className="text-slate-500">Actual Start</span>
                      <span className="font-semibold text-slate-800">{dateTime(actualStart)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Actual End</span>
                      <span className={`font-semibold ${actualEnd ? 'text-emerald-700' : 'text-slate-800'}`}>{dateTime(actualEnd)}</span>
                    </div>
                    {actualStart && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">{actualEnd ? 'Total Duration' : 'Running For'}</span>
                        <span className="font-bold text-slate-900">
                          {elapsedDays > 0 ? `${elapsedDays} d ` : ''}
                          {elapsedHours} h
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-5">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2.5 mb-3">Progress</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Stages completed</span>
                      <span className="font-semibold text-slate-800">{completedStages} / {totalStages}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">In progress · waiting QC · failed QC</span>
                      <span className="font-semibold text-slate-800">
                        {stageOf('IN_PROGRESS')} · {stageOf('QC_PENDING') + stageOf('COMPLETED_QC_PENDING')} · {stageOf('QC_FAILED')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Materials issued</span>
                      <span className="font-semibold text-slate-800">
                        {displayReservations.filter((r: any) => Number(r.issuedQty || 0) > 0).length} / {displayReservations.length} items
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Shop floor entries</span>
                      <span className="font-semibold text-slate-800">{shopFloorEntries.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── TAB 2: MATERIALS (MATERIAL RESERVATIONS) ── */}
        {activeTab === 'materials' && (
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
            <div className="p-4 sm:p-5 flex items-center justify-between border-b border-slate-100 bg-white">
              <div className="flex items-center gap-2.5">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                <div className="flex items-center gap-2.5">
                  <h3 className="text-base font-bold text-slate-800 tracking-tight">
                    Material Reservations
                  </h3>
                  {/* {allMaterialsIssued && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                      Materials Issued
                    </span>
                  )} */}
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => navigate(`/manufacturing/issue-queue?search=${encodeURIComponent(order.po_number || order.poNumber || '')}`)}
                  className="h-9 px-3.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
                  title="Open Store Keeper Material Issue Queue Workbench"
                >
                  <i className="ri-inbox-archive-line text-sm text-amber-600" />
                  Issue Queue
                </button>
                <button
                  type="button"
                  onClick={() => setShowBomExplosionModal(true)}
                  disabled={!order.bom_id}
                  className="h-9 px-3.5 rounded-lg border border-indigo-200 bg-indigo-50/70 hover:bg-indigo-100/80 text-indigo-700 text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
                  title="Open BOM Explosion & component shortage analysis"
                >
                  <i className="ri-node-tree text-sm text-indigo-600" />
                  BOM Explosion
                </button>
                <button
                  type="button"
                  onClick={handleReserveAll}
                  disabled={order.status === 'COMPLETED' || order.status === 'CANCELLED' || order.status === 'DRAFT' || isOnHold || displayReservations.length === 0}
                  className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  title={order.status === 'DRAFT' ? 'Material reservations unlock upon order confirmation' : isOnHold ? 'Production order is ON HOLD. Please resume order first.' : 'Reserve available stock for all components'}
                >
                  <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="3" x2="12" y2="15" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="5" y1="20" x2="19" y2="20" />
                  </svg>
                  Reserve All
                </button>
                {order.status === 'DRAFT' || isPlannedOrder ? null : hasRouting ? (
                  (() => {
                    const activeStagePending = stageGroupedMaterials?.stages.find(
                      (st) => !st.isLocked && !st.isCompleted && st.items.some((it) => it.queueStatus === 'PENDING' || it.queueStatus === 'PARTIAL')
                    );
                    const allRoutingIssued = stageGroupedMaterials?.stages.length
                      ? stageGroupedMaterials.stages.every((st) => st.isCompleted || (st.items.length > 0 && st.items.every((it) => it.queueStatus === 'ISSUED')))
                      : allMaterialsIssued;

                    if (allRoutingIssued) {
                      return (
                        <div
                          className="h-9 px-3.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-semibold shadow-2xs flex items-center gap-1.5"
                          title="All stage materials have been approved and issued by Store Keeper."
                        >
                          <i className="ri-checkbox-circle-fill text-sm text-emerald-600" />
                          Approved
                        </div>
                      );
                    }



                    return (
                      <button
                        type="button"
                        onClick={() => navigate(`/manufacturing/issue-queue?search=${encodeURIComponent(order.po_number || order.poNumber || '')}`)}
                        className="h-9 px-3.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
                        title="View pending material issue queue in Store Keeper workbench"
                      >
                        <i className="ri-inbox-archive-line text-sm text-amber-600" />
                        Stage Issue Queue
                      </button>
                    );
                  })()
                ) : allMaterialsIssued ? (
                  <button
                    type="button"
                    onClick={openIssueMaterialModal}
                    className="h-9 px-3.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
                    title="All materials have been issued to production floor. Click to view issue details."
                  >
                    <i className="ri-checkbox-circle-fill text-sm text-emerald-600" />
                    Materials Issued
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={openIssueMaterialModal}
                    disabled={
                      !allItemsReserved ||
                      order.status === 'COMPLETED' ||
                      order.status === 'CANCELLED' ||
                      order.status === 'DRAFT' ||
                      isOnHold ||
                      displayReservations.length === 0
                    }
                    className={`h-9 px-3.5 rounded-lg text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all ${
                      allItemsReserved && order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && order.status !== 'DRAFT' && !isOnHold
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 ring-2 ring-indigo-500/30 cursor-pointer shadow-sm'
                        : 'border border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-60'
                    }`}
                    title={
                      isOnHold
                        ? 'Production order is ON HOLD. Resume order to issue materials.'
                        : order.status === 'DRAFT'
                        ? 'Gate 2 Required: Please confirm order first'
                        : !allItemsReserved
                        ? 'Reserve every material (fully or partially) before issuing. Some items are still pending.'
                        : 'Issue materials to shop floor'
                    }
                  >
                    <i className={`ri-share-forward-box-line text-sm ${allItemsReserved && !isOnHold ? 'text-indigo-200' : 'text-slate-400'}`} />
                    Issue Material
                  </button>
                )}
              </div>
            </div>

            {/* DRAFT Material Notice Banner */}
            {order.status === 'DRAFT' && (
              <div className="mx-5 my-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200/90 text-xs text-amber-900 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-information-fill text-amber-600 text-base shrink-0" />
                  <div>
                    <p className="font-bold text-amber-900">DRAFT Order — Reservations &amp; Material Issuance Disabled</p>
                    <p className="text-amber-700 text-[11px] mt-0.5">
                      Material reservations and stock issues unlock once the order is confirmed. You can use <strong>BOM Explosion</strong> to analyze component requirements and shortages.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleConfirmOrder}
                  disabled={actionLoading}
                  className="h-8 px-3.5 rounded-lg bg-[#4f46e5] hover:bg-indigo-700 text-white font-semibold text-xs transition-colors shrink-0 cursor-pointer shadow-xs inline-flex items-center gap-1.5"
                >
                  <i className="ri-checkbox-circle-line text-sm" />
                  Confirm Order
                </button>
              </div>
            )}

            {/* ON_HOLD Material Notice Banner */}
            {isOnHold && (
              <div className="mx-5 my-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200/90 text-xs text-amber-900 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-pause-circle-fill text-amber-600 text-base shrink-0" />
                  <div>
                    <p className="font-bold text-amber-900">Order ON HOLD — Material Operations Paused</p>
                    <p className="text-amber-700 text-[11px] mt-0.5">
                      Material reserving and stock issuance to the shop floor are paused until the production order is resumed.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  disabled={actionLoading}
                  className="h-8 px-3.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs transition-colors shrink-0 cursor-pointer shadow-xs inline-flex items-center gap-1.5"
                >
                  <i className="ri-play-line text-sm" />
                  Resume Order
                </button>
              </div>
            )}

            {/* CANCELLED Material Notice Banner */}
            {order.status === 'CANCELLED' && (
              <div className="mx-5 my-4 p-3.5 rounded-xl bg-rose-50 border border-rose-200/90 text-xs text-rose-900 flex items-center gap-2.5">
                <i className="ri-close-circle-fill text-rose-600 text-base shrink-0" />
                <div>
                  <p className="font-bold text-rose-900">Order CANCELLED — All Material Reservations Released</p>
                  <p className="text-rose-700 mt-0.5">
                    Reserved stocks have been unreserved and returned to warehouse inventory. No further materials can be issued.
                  </p>
                </div>
              </div>
            )}

            {/* Finished units the issued material can make */}
            {maxProducible !== null && order.status !== 'DRAFT' && order.status !== 'CANCELLED' && !isPlannedOrder && (
              <div className={`mx-5 my-4 p-3.5 rounded-xl border text-xs flex flex-wrap items-center justify-between gap-3 ${!materialCapacity?.anyIssued
                ? 'bg-slate-50 border-slate-200 text-slate-700'
                : limitingMaterial
                ? 'bg-amber-50 border-amber-200/90 text-amber-900'
                : 'bg-emerald-50 border-emerald-200/90 text-emerald-900'}`}>
                <div className="flex items-start gap-2.5">
                  <i className={`${!materialCapacity?.anyIssued ? 'ri-information-line text-slate-500' : limitingMaterial ? 'ri-alert-fill text-amber-600' : 'ri-checkbox-circle-fill text-emerald-600'} text-base shrink-0`} />
                  <div>
                    <p className="font-bold">
                      {materialCapacity?.anyIssued
                        ? `Based on issued materials, maximum producible: ${maxProducible} of ${plannedQty} ${orderUnit}`
                        : 'No material issued yet — nothing can be produced until materials are issued'}
                    </p>
                    <p className="text-[11px] mt-0.5 opacity-90">
                      {!materialCapacity?.anyIssued
                        ? `Issue materials for all ${plannedQty} ${orderUnit}, or issue what is available to produce part of the order.`
                        : limitingMaterial
                        ? `Limiting material: ${limitingMaterial.itemName}${limitingStage ? ` at Stage ${limitingStage.stageNumber} (${limitingStage.stageName})` : ''} (${limitingMaterial.issuedQty} / ${limitingMaterial.requiredQty} ${limitingMaterial.unit} issued) · short by ${limitingMaterial.shortQty} ${limitingMaterial.unit}. Issue the rest to produce the remaining ${Math.max(0, plannedQty - maxProducible)} ${orderUnit}.`
                        : 'Enough material is issued for the full ordered quantity.'}
                      {' '}Produced so far: {Number(materialCapacity?.producedQty) || 0} {orderUnit}.
                    </p>
                    {capacityStages.length > 0 && materialCapacity?.anyIssued && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {capacityStages.map((st: any) => {
                          const stageMax = Number(st.maxProducible) || 0;
                          const full = !st.hasMaterials || stageMax >= plannedQty;
                          return (
                            <span
                              key={st.stageNumber}
                              title={st.limiting ? `${st.limiting.itemName}: ${st.limiting.issuedQty} / ${st.limiting.requiredQty} ${st.limiting.unit} issued` : undefined}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold bg-white ${full
                                ? 'border-emerald-200 text-emerald-800'
                                : stageMax > 0 ? 'border-amber-300 text-amber-800' : 'border-rose-200 text-rose-700'}`}
                            >
                              <i className={full ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
                              Stage {st.stageNumber} · {st.stageName}:{' '}
                              {st.hasMaterials ? `${stageMax} of ${plannedQty} ${orderUnit}` : 'no material'}
                              {st.limiting && <span className="font-normal opacity-80">({st.limiting.itemName} short {st.limiting.shortQty} {st.limiting.unit})</span>}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {limitingMaterial && materialCapacity?.anyIssued && (
                  <button
                    type="button"
                    onClick={() => setShowBomExplosionModal(true)}
                    className="h-8 px-3.5 rounded-lg border border-amber-300 bg-white hover:bg-amber-100 text-amber-800 font-semibold text-xs shrink-0 cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <i className="ri-shopping-cart-2-line text-sm" />
                    Check Shortage
                  </button>
                )}
              </div>
            )}

            {/* Stage-wise View Mode Toggle & Overview */}
            {hasRouting && stageGroupedMaterials && (
              <div className="mx-5 mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-600">View Mode:</span>
                  <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setMaterialsViewMode('stage')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                        materialsViewMode === 'stage'
                          ? 'bg-indigo-600 text-white shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <i className="ri-node-tree" />
                      Stage-wise (Auto Issue)
                    </button>
                    <button
                      type="button"
                      onClick={() => setMaterialsViewMode('flat')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                        materialsViewMode === 'flat'
                          ? 'bg-indigo-600 text-white shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <i className="ri-list-unordered" />
                      Flat List
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/manufacturing/issue-queue?search=${encodeURIComponent(order.po_number || order.poNumber || '')}`)}
                    className="h-8 px-3 rounded-lg border border-indigo-200 bg-white hover:bg-indigo-50 text-indigo-700 text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <i className="ri-share-forward-box-line text-sm text-indigo-600" />
                    Open Store Keeper Workbench &rarr;
                  </button>
                </div>
              </div>
            )}

            {/* Stage-wise View or Flat Table */}
            {hasRouting && stageGroupedMaterials && materialsViewMode === 'stage' ? (
              <div className="px-5 pb-5 space-y-4">
                {stageGroupedMaterials.stages.map((stage) => {
                  const isLocked = stage.isLocked;
                  const isCompleted = stage.isCompleted;
                  const isActive = stage.isActive;

                  return (
                    <div
                      key={stage.stageNumber}
                      className={`rounded-xl border overflow-hidden transition-all ${
                        isCompleted
                          ? 'border-emerald-200 bg-white'
                          : isLocked
                          ? 'border-slate-200 bg-slate-50/50'
                          : 'border-indigo-200 bg-white shadow-xs'
                      }`}
                    >
                      {/* Stage Header */}
                      <div
                        className={`px-4 py-3 flex flex-wrap items-center justify-between gap-3 border-b ${
                          isCompleted
                            ? 'bg-emerald-50/70 border-emerald-100 text-emerald-950'
                            : isLocked
                            ? 'bg-slate-100/80 border-slate-200 text-slate-700'
                            : 'bg-indigo-50/70 border-indigo-100 text-indigo-950'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                              isCompleted
                                ? 'bg-emerald-600 text-white'
                                : isLocked
                                ? 'bg-slate-300 text-slate-700'
                                : 'bg-indigo-600 text-white shadow-xs'
                            }`}
                          >
                            {stage.stageNumber}
                          </span>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm tracking-tight text-slate-900">
                                Stage {stage.stageNumber}: {stage.stageName}
                              </span>
                              {stage.woNumber && (
                                <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-600 shadow-2xs">
                                  {stage.woNumber}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {stage.items.length} material{stage.items.length === 1 ? '' : 's'} linked
                              {isLocked ? ' · Auto-enqueued when previous stage passes QC' : isActive ? ' · Active on shop floor / issue queue' : ''}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5">
                          {isCompleted ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs">
                              <i className="ri-checkbox-circle-fill text-emerald-600 text-sm" />
                              STAGE COMPLETED
                            </span>
                          ) : isLocked ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-200 text-slate-700 border border-slate-300 shadow-2xs">
                              <i className="ri-lock-2-line text-slate-600 text-sm" />
                              LOCKED (Awaiting Stage {stage.stageNumber - 1} QC Pass)
                            </span>
                          ) : stage.items.length > 0 && stage.items.every(it => it.queueStatus === 'ISSUED') ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs">
                              <i className="ri-checkbox-circle-fill text-emerald-600 text-sm" />
                              APPROVED
                            </span>
                          ) : stage.items.some(it => it.queueStatus === 'PENDING') ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 shadow-2xs animate-pulse" title="Stage materials pending Store Keeper approval & issue">
                              <i className="ri-time-line text-amber-600 text-sm" />
                              PENDING FOR VERIFICATION
                            </span>
                          ) : stage.items.some(it => it.queueStatus === 'PARTIAL') ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-300 shadow-2xs" title="Part of the stage material is issued; the store keeper issues the rest when stock arrives">
                              <i className="ri-contrast-2-line text-indigo-600 text-sm" />
                              PARTLY ISSUED
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-300 shadow-2xs">
                              <i className="ri-flashlight-line text-indigo-600 text-sm" />
                              ACTIVE STAGE
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stage Materials Table */}
                      {stage.items.length > 0 ? (() => {
                        const isStageAllApprovedOrPendingVerification = stage.items.length > 0 && stage.items.every(mr => {
                          const isQueuePending = mr.queueStatus === 'PENDING' || mr.queueStatus === 'PARTIAL';
                          const isIssued = mr.queueStatus === 'ISSUED' || (stage.isCompleted && !isQueuePending);
                          return isIssued || isQueuePending;
                        });
                        const showActionCol = !stage.isCompleted && !isStageAllApprovedOrPendingVerification && stage.items.some(mr => {
                          if (isLocked) return false;
                          const isQueuePending = mr.queueStatus === 'PENDING' || mr.queueStatus === 'PARTIAL';
                          const isIssued = mr.queueStatus === 'ISSUED' || (stage.isCompleted && !isQueuePending);
                          return !isIssued && !isQueuePending;
                        });

                        return (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 tracking-wide">Item Name</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Required Qty</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Available Stock</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Reserved Qty</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-indigo-700 tracking-wide">Issued Qty</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-amber-700 tracking-wide bg-amber-50/50">Pending Qty</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Max FG</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Unit</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Status</th>
                                {showActionCol && (
                                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 tracking-wide">Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100/80">
                              {stage.items.map((mr) => {
                                const reqQty = Number(mr.requiredQty || 0);
                                const resQty = Number(mr.reservedQty || 0);
                                const issQty = Number(mr.queueIssuedQty ?? 0);
                                const isQueuePending = mr.queueStatus === 'PENDING';
                                const isQueuePartial = mr.queueStatus === 'PARTIAL';
                                const isIssued = mr.queueStatus === 'ISSUED' || (stage.isCompleted && !isQueuePending && !isQueuePartial);
                                const queueRequired = Number(mr.queueItem?.required_qty ?? reqQty);
                                const pendingQty = isIssued ? 0 : isQueuePending ? reqQty
                                  : isQueuePartial ? Math.max(0, queueRequired - issQty)
                                  : Math.max(0, reqQty - resQty - issQty);
                                const isPartiallyIssued = isQueuePartial || (!isIssued && !isQueuePending && issQty > 0 && issQty < reqQty);
                                const isReserved = !isIssued && !isQueuePending && (mr.status === 'RESERVED' || resQty >= reqQty) && resQty > 0;
                                const isPartial = !isIssued && !isQueuePending && !isReserved && resQty > 0 && resQty < reqQty;
                                const isPending = !isReserved && !isPartial && !isIssued && !isPartiallyIssued && !isQueuePending;

                                return (
                                  <tr key={mr.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-5 py-3.5">
                                      <div className="text-sm font-semibold text-slate-800">{mr.itemName}</div>
                                      <div className="text-xs text-slate-400 font-normal mt-0.5">{mr.itemCode}</div>
                                    </td>
                                    <td className="px-4 py-3.5 text-right text-sm text-slate-600 font-normal">
                                      {reqQty.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3.5 text-right text-sm">
                                      {Number(mr.stockAvailable || 0) >= reqQty && Number(mr.stockAvailable || 0) > 0 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                          <i className="ri-checkbox-circle-line text-xs" />
                                          {Number(mr.stockAvailable || 0).toFixed(2)} {mr.unit}
                                        </span>
                                      ) : Number(mr.stockAvailable || 0) > 0 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                          <i className="ri-alert-line text-xs" />
                                          {Number(mr.stockAvailable || 0).toFixed(2)} {mr.unit}
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                          <i className="ri-close-circle-line text-xs" />
                                          0.00 {mr.unit}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3.5 text-right text-sm text-slate-700 font-semibold font-mono">
                                      {resQty.toFixed(2)}
                                    </td>
                                    <td className={`px-4 py-3.5 text-right text-sm font-mono font-semibold ${issQty > 0 ? 'text-indigo-700' : 'text-slate-400'}`}>
                                      {issQty.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3.5 text-right text-sm bg-amber-50/20">
                                      {pendingQty > 0 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                          {pendingQty.toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                          0.00
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3.5 text-right text-sm font-mono text-slate-600">
                                      {(() => {
                                        const cap = maxUnitsByItem[String(mr.itemId)];
                                        if (!cap || cap.maxUnits === null || cap.maxUnits === undefined) return '—';
                                        return `${Math.min(Number(cap.maxUnits), plannedQty)}`;
                                      })()}
                                    </td>
                                    <td className="px-4 py-3.5 text-left text-sm text-slate-600 font-normal">
                                      {mr.unit}
                                    </td>
                                    <td className="px-4 py-3.5 text-left">
                                      {isLocked ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-slate-300 bg-slate-100 text-slate-600" title="Locked until previous stage passes QC">
                                          <i className="ri-lock-2-line text-slate-500 text-xs" />
                                          LOCKED
                                        </span>
                                      ) : isIssued ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700">
                                          <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                                          APPROVED
                                        </span>
                                      ) : isQueuePending ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-700 animate-pulse" title="In Store Keeper Issue Queue">
                                          <i className="ri-time-line text-amber-600 text-xs" />
                                          PENDING FOR VERIFICATION
                                        </span>
                                      ) : isPartiallyIssued ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700"
                                          title={isQueuePartial ? 'Part issued; the store keeper issues the rest when stock arrives' : undefined}>
                                          <i className="ri-truck-line text-indigo-600 text-xs" />
                                          PARTIAL
                                        </span>
                                      ) : isReserved ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700">
                                          <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                                          RESERVED
                                        </span>
                                      ) : isPartial ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-700">
                                          <i className="ri-alert-fill text-amber-600 text-xs" />
                                          PARTIAL
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-slate-300 bg-slate-100 text-slate-600">
                                          <i className="ri-close-circle-line text-slate-500 text-xs" />
                                          UNRESERVED
                                        </span>
                                      )}
                                    </td>
                                    {showActionCol && (
                                      <td className="px-5 py-3.5 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                          {order.status === 'CANCELLED' ? (
                                            <span className="text-xs text-slate-400 font-medium italic">Unreserved</span>
                                          ) : isQueuePending || isQueuePartial ? null : isReserved ? (
                                            <button
                                              type="button"
                                              onClick={() => handleReleaseOne(mr)}
                                              disabled={actionLoading || isOnHold}
                                              className="h-7 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-700 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
                                            >
                                              <i className="ri-arrow-go-back-line text-xs" />
                                              Unreserve
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() => handleReserveOne(mr)}
                                              disabled={actionLoading || isOnHold}
                                              className="h-7 px-2.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
                                            >
                                              <i className="ri-bookmark-fill text-xs" />
                                              Reserve
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        );
                      })() : (
                        <div className="px-5 py-5 text-center text-xs text-slate-400 bg-slate-50/40">
                          <p className="font-medium text-slate-500">No specific BOM materials mapped to this stage.</p>
                          <p className="mt-0.5">Link materials in Routing Setup &rarr; Stage Form.</p>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* General Materials Card */}
                {stageGroupedMaterials.generalMaterials.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-xs">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold shrink-0">
                          <i className="ri-archive-line text-sm" />
                        </div>
                        <div>
                          <span className="font-bold text-sm text-slate-900">General Materials</span>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {stageGroupedMaterials.generalMaterials.length} item(s) unmapped to specific stage (queued at PRD start)
                          </p>
                        </div>
                      </div>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-md bg-white border border-slate-200 text-slate-600">
                        General Queue
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 tracking-wide">Item Name</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Required Qty</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Available Stock</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Reserved Qty</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-indigo-700 tracking-wide">Issued Qty</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-amber-700 tracking-wide bg-amber-50/50">Pending Qty</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Max FG</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Unit</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">Status</th>
                            <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 tracking-wide">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/80">
                          {stageGroupedMaterials.generalMaterials.map((mr) => {
                            const reqQty = Number(mr.requiredQty || 0);
                            const resQty = Number(mr.reservedQty || 0);
                            const issQty = Number(mr.issuedQty || 0);
                            const isIssued = mr.status === 'ISSUED' || (issQty >= reqQty && reqQty > 0);
                            const pendingQty = isIssued ? 0 : Math.max(0, reqQty - resQty - issQty);
                            const isPartiallyIssued = mr.status === 'PARTIALLY ISSUED' || (issQty > 0 && issQty < reqQty);
                            const isReserved = (mr.status === 'RESERVED' || (resQty >= reqQty && reqQty > 0)) && resQty > 0 && !isIssued;
                            const isPartial = (mr.status === 'PARTIAL' || (resQty > 0 && resQty < reqQty)) && !isReserved && !isIssued;
                            const isPending = !isReserved && !isPartial && !isIssued && !isPartiallyIssued;

                            return (
                              <tr key={mr.id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-5 py-3.5">
                                  <div className="text-sm font-semibold text-slate-800">{mr.itemName}</div>
                                  <div className="text-xs text-slate-400 font-normal mt-0.5">{mr.itemCode}</div>
                                </td>
                                <td className="px-4 py-3.5 text-right text-sm text-slate-600 font-normal">
                                  {reqQty.toFixed(2)}
                                </td>
                                <td className="px-4 py-3.5 text-right text-sm">
                                  {Number(mr.stockAvailable || 0) >= reqQty && Number(mr.stockAvailable || 0) > 0 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <i className="ri-checkbox-circle-line text-xs" />
                                      {Number(mr.stockAvailable || 0).toFixed(2)} {mr.unit}
                                    </span>
                                  ) : Number(mr.stockAvailable || 0) > 0 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                      <i className="ri-alert-line text-xs" />
                                      {Number(mr.stockAvailable || 0).toFixed(2)} {mr.unit}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                      <i className="ri-close-circle-line text-xs" />
                                      0.00 {mr.unit}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3.5 text-right text-sm text-slate-700 font-semibold font-mono">
                                  {resQty.toFixed(2)}
                                </td>
                                <td className={`px-4 py-3.5 text-right text-sm font-mono font-semibold ${issQty > 0 ? 'text-indigo-700' : 'text-slate-400'}`}>
                                  {issQty.toFixed(2)}
                                </td>
                                <td className="px-4 py-3.5 text-right text-sm bg-amber-50/20">
                                  {pendingQty > 0 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                      {pendingQty.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      0.00
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3.5 text-right text-sm font-mono text-slate-600">
                                  {(() => {
                                    const cap = maxUnitsByItem[String(mr.itemId)];
                                    if (!cap || cap.maxUnits === null || cap.maxUnits === undefined) return '—';
                                    return `${Math.min(Number(cap.maxUnits), plannedQty)}`;
                                  })()}
                                </td>
                                <td className="px-4 py-3.5 text-left text-sm text-slate-600 font-normal">
                                  {mr.unit}
                                </td>
                                <td className="px-4 py-3.5 text-left">
                                  {mr.queueStatus === 'ISSUED' || isIssued ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700">
                                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                                      APPROVED
                                    </span>
                                  ) : mr.queueStatus === 'PENDING' ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-700 animate-pulse" title="In Store Keeper Issue Queue">
                                      <i className="ri-time-line text-amber-600 text-xs" />
                                      PENDING FOR VERIFICATION
                                    </span>
                                  ) : isPartiallyIssued ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700">
                                      <i className="ri-truck-line text-indigo-600 text-xs" />
                                      PARTIAL
                                    </span>
                                  ) : isReserved ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700">
                                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                                      RESERVED
                                    </span>
                                  ) : isPartial ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-700">
                                      <i className="ri-alert-fill text-amber-600 text-xs" />
                                      PARTIAL
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-slate-300 bg-slate-100 text-slate-600">
                                      <i className="ri-close-circle-line text-slate-500 text-xs" />
                                      UNRESERVED
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3.5 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    {order.status === 'CANCELLED' ? (
                                      <span className="text-xs text-slate-400 font-medium italic">Unreserved</span>
                                    ) : isIssued ? (
                                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200">
                                        <i className="ri-checkbox-circle-fill text-xs" />
                                        Approved
                                      </span>
                                    ) : mr.queueId && mr.queueStatus === 'PENDING' ? (
                                      <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200" title="Material issue is pending approval by Store Keeper">
                                        <i className="ri-time-line text-xs" />
                                        Pending for Verification
                                      </span>
                                    ) : mr.queueId && mr.queueStatus === 'PARTIAL' ? (
                                      <span className="inline-flex items-center gap-1 text-xs text-indigo-700 font-semibold bg-indigo-50 px-2.5 py-1 rounded-md border border-indigo-200" title="Part issued; the store keeper issues the rest when stock arrives">
                                        <i className="ri-contrast-2-line text-xs" />
                                        Partly Issued
                                      </span>
                                    ) : isReserved ? (
                                      <button
                                        type="button"
                                        onClick={() => handleReleaseOne(mr)}
                                        disabled={actionLoading || isOnHold}
                                        className="h-7 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-700 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
                                      >
                                        <i className="ri-arrow-go-back-line text-xs" />
                                        Unreserve
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleReserveOne(mr)}
                                        disabled={actionLoading || isOnHold}
                                        className="h-7 px-2.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
                                      >
                                        <i className="ri-bookmark-fill text-xs" />
                                        Reserve
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Item Name</th>
                      <th className="text-right px-4 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Required Qty</th>
                      <th className="text-right px-4 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Available Stock</th>
                      <th className="text-right px-4 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Reserved Qty</th>
                      <th className="text-right px-4 py-3.5 text-xs font-semibold text-indigo-700 tracking-wide">Issued Qty</th>
                      <th className="text-right px-4 py-3.5 text-xs font-semibold text-amber-700 tracking-wide bg-amber-50/50">Pending Qty</th>
                      <th className="text-right px-4 py-3.5 text-xs font-semibold text-slate-500 tracking-wide" title="Finished units this material's issued quantity can make">Max FG Possible</th>
                      <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Unit</th>
                      <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Status</th>
                      <th className="text-right px-5 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/80">
                    {displayReservations.map((mr) => {
                      const reqQty = Number(mr.requiredQty || 0);
                      const resQty = Number(mr.reservedQty || 0);
                      const issQty = Number(mr.issuedQty || 0);
                      const isIssued = mr.status === 'ISSUED' || (issQty >= reqQty && reqQty > 0);
                      const pendingQty = isIssued ? 0 : Math.max(0, reqQty - resQty - issQty);
                      const isPartiallyIssued = mr.status === 'PARTIALLY ISSUED' || (issQty > 0 && issQty < reqQty);
                      const isReserved = (mr.status === 'RESERVED' || (resQty >= reqQty && reqQty > 0)) && resQty > 0 && !isIssued;
                      const isPartial = (mr.status === 'PARTIAL' || (resQty > 0 && resQty < reqQty)) && !isReserved && !isIssued;
                      const isPending = !isReserved && !isPartial && !isIssued && !isPartiallyIssued;

                      return (
                        <tr key={mr.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-4">
                            <div className="text-sm font-semibold text-slate-800">{mr.itemName}</div>
                            <div className="flex items-center gap-1.5 flex-wrap mt-1">
                              <ItemTypeBadge type={(mr as any).itemType || (mr as any).item_type} />
                              <span className="text-xs text-slate-400 font-normal">{mr.itemCode}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right text-sm text-slate-600 font-normal">
                            {Number(mr.requiredQty || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 text-right text-sm">
                            {Number(mr.stockAvailable || 0) >= reqQty && Number(mr.stockAvailable || 0) > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <i className="ri-checkbox-circle-line text-xs" />
                                {Number(mr.stockAvailable || 0).toFixed(2)} {mr.unit}
                              </span>
                            ) : Number(mr.stockAvailable || 0) > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                <i className="ri-alert-line text-xs" />
                                {Number(mr.stockAvailable || 0).toFixed(2)} {mr.unit}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                <i className="ri-close-circle-line text-xs" />
                                0.00 {mr.unit} (Out of Stock)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right text-sm text-slate-700 font-semibold font-mono">
                            {Number(mr.reservedQty || 0).toFixed(2)}
                          </td>
                          <td className={`px-4 py-4 text-right text-sm font-mono font-semibold ${issQty > 0 ? 'text-indigo-700' : 'text-slate-400'}`}>
                            {issQty.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 text-right text-sm bg-amber-50/20">
                            {pendingQty > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-mono font-semibold bg-amber-50 text-amber-700 border border-amber-200" title={`Pending to be reserved: ${pendingQty.toFixed(2)} ${mr.unit}`}>
                                <i className="ri-time-line text-xs" />
                                {pendingQty.toFixed(2)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-mono font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title="Fully reserved">
                                <i className="ri-check-line text-xs" />
                                0.00
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right text-sm">
                            {(() => {
                              const cap = maxUnitsByItem[String(mr.itemId)];
                              if (!cap || cap.maxUnits === null || cap.maxUnits === undefined) return <span className="text-slate-400">—</span>;
                              return cap.isBottleneck ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200" title={`${cap.perUnitQty} ${mr.unit || ''} needed per finished unit — this material limits the order`}>
                                  <i className="ri-alert-line text-xs" />
                                  {cap.maxUnits} {orderUnit} · Bottleneck
                                </span>
                              ) : (
                                <span className="font-mono font-semibold text-slate-700" title={`${cap.perUnitQty} ${mr.unit || ''} needed per finished unit`}>
                                  {Math.min(Number(cap.maxUnits), plannedQty)} {orderUnit}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-4 text-left text-sm text-slate-600 font-normal">
                            {mr.unit}
                          </td>
                          <td className="px-4 py-4 text-left">
                            {isIssued && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-blue-300 bg-blue-50 text-blue-700">
                                <i className="ri-truck-fill text-blue-600 text-xs" />
                                ISSUED
                              </span>
                            )}
                            {isPartiallyIssued && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700">
                                <i className="ri-truck-line text-indigo-600 text-xs" />
                                PARTIALLY ISSUED
                              </span>
                            )}
                            {isReserved && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700">
                                <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                                RESERVED
                              </span>
                            )}
                            {isPartial && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-700" title={`Reserved: ${resQty.toFixed(2)} / Required: ${reqQty.toFixed(2)}`}>
                                <i className="ri-alert-fill text-amber-600 text-xs" />
                                PARTIALLY RESERVED
                              </span>
                            )}
                            {isPending && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-rose-300 bg-rose-50 text-rose-600">
                                <i className="ri-close-circle-line text-rose-500 text-xs" />
                                PENDING
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {order.status === 'CANCELLED' ? (
                                <span className="text-xs text-slate-400 font-medium italic">Unreserved (Cancelled)</span>
                              ) : isIssued ? (
                                <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">
                                  <i className="ri-check-line text-xs" />
                                  On Shop Floor
                                </span>
                              ) : isReserved ? (
                                <button
                                  type="button"
                                  onClick={() => handleReleaseOne(mr)}
                                  disabled={actionLoading || isOnHold}
                                  className="h-8 px-3 rounded-lg border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-700 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={isOnHold ? 'Order is ON HOLD' : 'Unreserve stock and release back to warehouse'}
                                >
                                  <i className="ri-arrow-go-back-line text-xs" />
                                  Unreserve
                                </button>
                              ) : isPartial ? (
                                <div className="flex items-center justify-end gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => handleReleaseOne(mr)}
                                    disabled={actionLoading || isOnHold}
                                    className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-700 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={isOnHold ? 'Order is ON HOLD' : 'Unreserve stock and release back to warehouse'}
                                  >
                                    <i className="ri-arrow-go-back-line text-xs" />
                                    Unreserve
                                  </button>
                                  {Number(mr.stockAvailable || 0) > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => handleReserveOne(mr)}
                                      disabled={actionLoading || isOnHold}
                                      className="h-8 px-2.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={isOnHold ? 'Order is ON HOLD' : 'Reserve available stock from warehouse'}
                                    >
                                      <i className="ri-bookmark-fill text-xs" />
                                      Reserve
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleReserveOne(mr)}
                                  disabled={actionLoading || isOnHold}
                                  className="h-8 px-3 rounded-lg border border-emerald-300 bg-emerald-50/70 hover:bg-emerald-100/90 text-emerald-700 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={isOnHold ? 'Order is ON HOLD' : 'Reserve stock from warehouse'}
                                >
                                  <i className="ri-bookmark-fill text-xs text-emerald-600" />
                                  Reserve
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {displayReservations.length === 0 && (
                      <tr>
                        <td colSpan={10} className="px-6 py-12 text-center text-slate-400">
                          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                            <i className="ri-inbox-line text-xl text-slate-300" />
                          </div>
                          <p className="text-sm font-medium text-slate-600">
                            {order.status === 'DRAFT' ? 'Material Reservations Disabled (DRAFT)' : 'No material reservations yet.'}
                          </p>
                          <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                            {order.status === 'DRAFT'
                              ? 'BOM materials will explode and reserve automatically when order is confirmed. Click "BOM Explosion" above to preview component requirements.'
                              : 'No BOM materials linked to this order.'}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 3: WORK ORDERS (ROUTING STAGES) ── */}
        {activeTab === 'workorders' && (
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2.5">
                <i className="ri-git-merge-line text-[#4f46e5] text-lg" />
                <h3 className="text-base font-bold text-slate-800 tracking-tight">Work Orders &amp; Routing Stages</h3>
              </div>
            </div>

            {/* ON_HOLD Work Orders Notice Banner */}
            {isOnHold && (
              <div className="mx-5 my-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200/90 text-xs text-amber-900 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-pause-circle-fill text-amber-600 text-base shrink-0" />
                  <div>
                    <p className="font-bold text-amber-900">Work Orders Locked — Production is ON HOLD</p>
                    <p className="text-amber-700 text-[11px] mt-0.5">
                      Starting stages, completions, rework, and quality checks are paused until the order is resumed.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  disabled={actionLoading}
                  className="h-8 px-3.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs transition-colors shrink-0 cursor-pointer shadow-xs inline-flex items-center gap-1.5"
                >
                  <i className="ri-play-line text-sm" />
                  Resume Order
                </button>
              </div>
            )}

            {order.status === 'DRAFT' ? (
              <div className="p-12 text-center space-y-4">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-indigo-50 border border-indigo-200 text-[#4f46e5]">
                  <i className="ri-git-merge-line text-2xl" />
                </div>
                <div className="max-w-md mx-auto space-y-1">
                  <h4 className="text-base font-bold text-slate-800">Work Orders Not Generated (DRAFT)</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    This production order is in draft status. Routing stages and operational work orders will be automatically generated upon order confirmation.
                  </p>
                </div>
                <button
                  onClick={handleConfirmOrder}
                  disabled={actionLoading}
                  className="h-9 px-4 rounded-xl bg-[#4f46e5] hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <i className="ri-checkbox-circle-line text-sm" />
                  Confirm Order to Generate Work Orders
                </button>
              </div>
            ) : order.status === 'CANCELLED' ? (
              <div className="py-12 text-center text-slate-400 space-y-2">
                <i className="ri-close-circle-line text-3xl text-rose-400" />
                <p className="text-sm font-semibold text-slate-700">Work Orders Inactive (Cancelled)</p>
                <p className="text-xs text-slate-400 max-w-sm mx-auto">
                  This production order has been cancelled. Work orders are locked and no stages can be started.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/70 text-slate-500 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3.5 text-left">WO No</th>
                      <th className="px-5 py-3.5 text-left">Stage</th>
                      <th className="px-5 py-3.5 text-center">Seq</th>
                      <th className="px-5 py-3.5 text-left">Work Center / Machine</th>
                      <th className="px-5 py-3.5 text-left">Operator</th>
                      <th className="px-5 py-3.5 text-right">Produced / Planned</th>
                      <th className="px-5 py-3.5 text-center">Status</th>
                      <th className="px-5 py-3.5 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {workOrders.map((wo: any, idx: number) => {
                      const seq = wo.stage_number || (idx + 1);
                      const woPlanned = Number(wo.planned_qty) || plannedQty;
                      const woCompleted = Number(wo.completed_qty) || 0;
                      const isCompleted = wo.status === 'COMPLETED' || isCompletedOrder;
                      const isInProgress = wo.status === 'IN_PROGRESS';
                      const isQcPending = wo.status === 'QC_PENDING' || wo.status === 'COMPLETED_QC_PENDING';
                      const isQcFailed = wo.status === 'QC_FAILED';

                      // Sequential stage check
                      const prevStages = workOrders.filter((w: any) => (w.stage_number || 0) < seq);
                      const uncompletedPrev = prevStages.find((w: any) => w.status !== 'COMPLETED');
                      const isLockedStage = Boolean(wo.is_locked || uncompletedPrev);

                      const stageData = stageGroupedMaterials?.stages?.find((s: any) => Number(s.stageNumber) === Number(seq));
                      const stageHasMaterials = Boolean(stageData && stageData.items && stageData.items.length > 0);
                      const isStageMaterialsIssued = !stageHasMaterials || stageData.items.every((m: any) => ['ISSUED', 'PARTIAL'].includes(m.queueStatus) || (stageData.isCompleted && m.queueStatus !== 'PENDING'));

                      return (
                        <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-5 py-4 font-semibold text-indigo-600">
                            {wo.wo_number}
                            {Number(wo.batch_no) > 1 && (
                              <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 align-middle" title="Units started after additional material was issued">
                                Batch {wo.batch_no}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 font-semibold text-slate-800">
                            {wo.stage_name}
                            {isCompleted && (
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                                <i className="ri-checkbox-circle-fill text-emerald-600" /> Stage Completed &amp; Verified
                              </div>
                            )}
                            {isInProgress && (
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-blue-700 font-medium">
                                <i className="ri-loader-4-line animate-spin text-blue-600" /> In Progress on Shop Floor
                              </div>
                            )}
                            {isQcPending && (
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-purple-700 font-medium">
                                <i className="ri-microscope-line text-purple-600" /> Waiting for QC Inspection
                              </div>
                            )}
                            {isQcFailed && (
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-rose-700 font-medium">
                                <i className="ri-close-circle-fill text-rose-600" /> QC Failed — Rework Needed
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center font-bold text-slate-600">{seq}</td>
                          <td className="px-5 py-4 text-slate-600">{[workCenterLookup.labelOf(wo.work_center_id, wo.work_center_name), wo.machine_name].filter(Boolean).join(' / ') || '—'}</td>
                          <td className="px-5 py-4 text-slate-600">{wo.operator_name || <span className="text-slate-400">Pending</span>}</td>
                          <td className="px-5 py-4 text-right font-medium">
                            <span className={woCompleted > 0 ? "text-emerald-700 font-bold" : "text-slate-800 font-bold"}>
                              {woCompleted}
                            </span> / {woPlanned} Pcs
                          </td>
                          <td className="px-5 py-4 text-center">
                            {order.status === 'CANCELLED' || wo.status === 'CANCELLED' ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                <i className="ri-close-circle-line text-rose-600 text-xs" /> CANCELLED
                              </span>
                            ) : isCompleted ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                                <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" /> COMPLETED
                              </span>
                            ) : isInProgress ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                                <i className="ri-loader-4-line animate-spin text-blue-600 text-xs" /> IN PROGRESS
                              </span>
                            ) : isQcPending ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                                <i className="ri-microscope-line text-purple-600 text-xs" /> QC PENDING
                              </span>
                            ) : isQcFailed ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                <i className="ri-close-circle-fill text-rose-600 text-xs" /> QC FAILED
                              </span>
                            ) : isLockedStage ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200 inline-flex items-center gap-1">
                                <i className="ri-lock-line text-slate-400 text-xs" /> LOCKED
                              </span>
                            ) : wo.assigned_at ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                                <i className="ri-user-follow-line text-blue-600 text-xs" /> ASSIGNED
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200 inline-flex items-center gap-1">
                                <i className="ri-calendar-line text-slate-400 text-xs" /> PLANNED
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center">
                            {order.status === 'CANCELLED' || wo.status === 'CANCELLED' ? (
                              <span className="text-xs text-slate-400 font-medium">Locked (Cancelled)</span>
                            ) : isCompleted ? (
                              <span className="text-emerald-600 font-semibold text-xs inline-flex items-center gap-1">
                                <i className="ri-check-double-line text-sm" /> Done
                              </span>
                            ) : isInProgress ? (
                              <button
                                onClick={() => handleConfirmCompleteWorkOrder(wo)}
                                disabled={actionLoading || isCompletedOrder || isOnHold}
                                className="h-8 px-3.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold text-xs inline-flex items-center gap-1 mx-auto cursor-pointer transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                title={isOnHold ? 'Order is ON HOLD. Resume order to complete.' : 'Complete'}
                              >
                                <i className="ri-check-line text-xs" /> Complete
                              </button>
                            ) : isQcPending ? (
                              <button
                                onClick={() => {
                                  const currentUrl = window.location.pathname + window.location.search;
                                  navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(wo.wo_number)}&inspection_id=${wo.qc_inspection_id || ''}&return_url=${encodeURIComponent(currentUrl)}`);
                                }}
                                disabled={actionLoading || isCompletedOrder || isOnHold}
                                className="h-8 px-3.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-semibold text-xs inline-flex items-center gap-1 mx-auto cursor-pointer transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                title={isOnHold ? 'Order is ON HOLD. Resume order to inspect QC.' : 'Inspect QC'}
                              >
                                <i className="ri-microscope-line text-xs" /> Inspect QC
                              </button>
                            ) : isQcFailed ? (
                              <div className="inline-flex items-center gap-1.5 mx-auto">
                                <button
                                  onClick={() => {
                                    const currentUrl = window.location.pathname + window.location.search;
                                    navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(wo.wo_number)}&inspection_id=${wo.qc_inspection_id || ''}&return_url=${encodeURIComponent(currentUrl)}`);
                                  }}
                                  disabled={actionLoading || isCompletedOrder || isOnHold}
                                  className="h-8 px-3 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-semibold text-xs inline-flex items-center gap-1 cursor-pointer transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={isOnHold ? 'Order is ON HOLD. Resume order to inspect QC.' : 'Inspect QC'}
                                >
                                  <i className="ri-microscope-line text-xs" /> Inspect QC
                                </button>
                                <button
                                  onClick={() => openReworkForm(wo)}
                                  disabled={actionLoading || isCompletedOrder || isOnHold}
                                  className="h-8 px-3 rounded-lg bg-rose-600 text-white hover:bg-rose-700 font-semibold text-xs inline-flex items-center gap-1 cursor-pointer transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={isOnHold ? 'Order is ON HOLD. Resume order first.' : 'Rework'}
                                >
                                  <i className="ri-restart-line text-xs" /> Rework
                                </button>
                              </div>
                            ) : isLockedStage ? (
                              <span
                                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-400 border border-slate-200 inline-flex items-center gap-1 cursor-not-allowed mx-auto select-none"
                                title={
                                  uncompletedPrev
                                    ? `Stage ${uncompletedPrev.stage_number} (${uncompletedPrev.stage_name}) is ${uncompletedPrev.status}. Complete previous stage to unlock.`
                                    : 'Locked: Complete previous stage and pass QC to unlock this stage'
                                }
                              >
                                <i className="ri-lock-line text-xs" /> Locked
                              </span>
                            ) : (
                              <button
                                onClick={() => openStartWorkOrderModal(wo)}
                                disabled={
                                  actionLoading ||
                                  isCompletedOrder ||
                                  isOnHold ||
                                  order.status === 'DRAFT' ||
                                  order.status === 'CANCELLED' ||
                                  isPlannedOrder ||
                                  (Boolean(wo.assigned_at) && (!hasIssuedAnyMaterials || !isStageMaterialsIssued))
                                }
                                title={
                                  isOnHold
                                    ? 'Order is ON HOLD. Resume order first.'
                                    : order.status === 'DRAFT'
                                      ? 'Gate 2 Required: Please confirm order first'
                                      : order.status === 'CANCELLED'
                                        ? 'Order is Cancelled'
                                        : isPlannedOrder
                                          ? 'Start the Production Order first'
                                        : wo.assigned_at && (!hasIssuedAnyMaterials || !isStageMaterialsIssued)
                                          ? `Gate 3 Required: Please issue materials for Stage ${seq} before starting work order`
                                          : wo.assigned_at ? 'Start Work Order' : 'Assign crew, machine and planned time'
                                }
                                className="h-8 px-3.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-xs inline-flex items-center gap-1 mx-auto cursor-pointer transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {wo.assigned_at
                                  ? <><i className="ri-play-line text-xs" /> Start WO</>
                                  : <><i className="ri-user-shared-line text-xs" /> Assign WO</>}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 4: SHOP FLOOR TIMELINE ── */}
        {activeTab === 'shopfloor' && (
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800 tracking-tight flex items-center gap-2">
                <i className="ri-history-line text-[#4f46e5]" />
                Shop Floor Activity Log &amp; Timeline ({shopFloorEntries.length})
              </h3>
            </div>

            {/* ON_HOLD Notice Banner in Shop Floor */}
            {isOnHold && (
              <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-pause-circle-fill text-amber-600 text-base shrink-0" />
                  <div>
                    <strong className="text-amber-900">Shop Floor Paused (Order ON HOLD)</strong>
                    <p className="text-amber-700 text-[11px] mt-0.5">
                      Production logging, scrap entries, and machine downtime tracking are paused until the order is resumed.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  disabled={actionLoading}
                  className="h-8 px-3.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs transition-colors shrink-0 cursor-pointer shadow-xs inline-flex items-center gap-1.5"
                >
                  <i className="ri-play-line text-sm" />
                  Resume Order
                </button>
              </div>
            )}

            {order.status === 'DRAFT' ? (
              <div className="py-12 text-center space-y-3">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-slate-50 border border-slate-200 text-slate-400">
                  <i className="ri-history-line text-2xl" />
                </div>
                <div className="max-w-md mx-auto space-y-1">
                  <h4 className="text-base font-bold text-slate-800">Shop Floor Activity Disabled (DRAFT)</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    No shop floor entries, material movements, or downtime logs can be created while in DRAFT status. Confirm the order to begin shop floor tracking.
                  </p>
                </div>
                <button
                  onClick={handleConfirmOrder}
                  disabled={actionLoading}
                  className="h-9 px-4 rounded-xl bg-[#4f46e5] hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <i className="ri-checkbox-circle-line text-sm" />
                  Confirm Order
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* If Order is completed, show completion header */}
                {isCompletedOrder && (
                  <div className="p-4 rounded-xl bg-emerald-600 text-white shadow-xs flex items-start justify-between text-xs">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-emerald-100 text-sm flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill text-white text-base" /> COMPLETED
                        </span>
                        <span className="font-bold text-white uppercase tracking-wider text-sm">PRODUCTION COMPLETED</span>
                        <span className="px-2.5 py-0.5 rounded-full bg-emerald-950/40 text-emerald-100 font-semibold text-xs border border-emerald-300/40">
                          {completedQty} Units Finished
                        </span>
                      </div>
                      <p className="text-emerald-50 font-medium">
                        {completedQty} Units produced · Batch: <strong className="text-white">{order.batch_number || `B-${order.po_number}`}</strong> created
                      </p>
                      <p className="text-emerald-100 text-xs">
                        Finished Goods Stock updated (+{completedQty} Units) · Work In Progress (WIP) Stock Cleared
                      </p>
                    </div>
                  </div>
                )}

                {/* Real Shop Floor Entries */}
                {shopFloorEntries && shopFloorEntries.length > 0 ? (
                  shopFloorEntries.map((entry: any, eIdx: number) => {
                    const isMatIssue = ['MATERIAL_ISSUE', 'Material Issue'].includes(entry.stage_name) || entry.entry_type === 'MATERIAL_ISSUE';
                    const isQc = entry.stage_name?.toLowerCase().includes('qc') || entry.stage_name?.toLowerCase().includes('inspection');
                    return (
                      <div
                        key={eIdx}
                        className={`p-4 rounded-xl border flex items-start justify-between text-xs sm:text-sm transition-colors ${isMatIssue
                          ? 'bg-indigo-50/50 border-indigo-200'
                          : isQc
                            ? 'bg-purple-50/50 border-purple-200'
                            : 'bg-slate-50 border-slate-200'
                          }`}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-700">
                              {String(entry.start_time || entry.startTime || '').slice(0, 5)}
                            </span>
                            <span className="font-bold text-slate-800 uppercase tracking-wider text-xs">
                              {String(entry.stage_name || entry.stageName || '').replace(/_/g, ' ')}
                            </span>
                            {Number(entry.produced_qty || entry.producedQty) > 0 && (
                              <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold text-xs">
                                {isMatIssue ? `${entry.produced_qty || entry.producedQty} issued` : `+${entry.produced_qty || entry.producedQty} produced`}
                              </span>
                            )}
                            {Number(entry.rejected_qty || entry.rejectedQty) > 0 && (
                              <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold text-xs">
                                {entry.rejected_qty || entry.rejectedQty} Rejected
                              </span>
                            )}
                          </div>
                          <p className="text-slate-600 text-xs">
                            {isMatIssue ? 'Issued by' : 'Operator'}: <span className="font-semibold text-slate-800">{entry.operator_name || entry.operatorName || '—'}</span>
                            {(entry.machine_name || entry.machineName) && ` · Machine: ${entry.machine_name || entry.machineName}`}
                          </p>
                          {entry.notes && (
                            <p className="text-slate-500 text-xs">
                              {entry.notes}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">
                          {entry.date ? formatDateIST(entry.date) : ''}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  !isCompletedOrder && (
                    <div className="py-12 text-center text-slate-400 space-y-2">
                      <i className="ri-history-line text-3xl text-slate-300" />
                      <p className="text-sm font-semibold text-slate-600">No shop floor activity logged yet</p>
                      <p className="text-xs text-slate-400 max-w-sm mx-auto">
                        Activity log will record live material issues, stage completions, QC passes, and production entries once work begins.
                      </p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: QUALITY CONTROL ── */}
        {activeTab === 'qc' && (
          <QualityControlTab
            order={order}
            workOrders={workOrders}
            isCompletedOrder={isCompletedOrder}
            onOpenQcInspectionModal={(wo) => {
              const currentUrl = window.location.pathname + window.location.search;
              navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(wo?.wo_number || '')}&inspection_id=${wo?.qc_inspection_id || ''}&return_url=${encodeURIComponent(currentUrl)}`);
            }}
            onConfirmOrder={handleConfirmOrder}
            actionLoading={actionLoading}
          />
        )}

        {/* ── SCRAP: rejected units sent to scrap, process waste, disposal ── */}
        {activeTab === 'scrap' && order?.id && <ScrapTab productionOrderId={order.id} />}

        {/* ── TAB 5: COSTING (FINAL REPORT WITH VARIANCE & GRADE A) ── */}
        {activeTab === 'costing' && (
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-800 tracking-tight flex items-center gap-2">
                <i className="ri-money-rupee-circle-line text-[#4f46e5]" />
                {isCompletedOrder ? 'Costing Summary & Variance Analysis' : 'Standard Cost (Planned Budget)'}
              </h3>
              <div className="flex items-center gap-2.5">
                <span className={`px-3.5 py-1 rounded-full text-xs font-semibold border flex items-center gap-1.5 shadow-2xs ${isCompletedOrder
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : isOnHold
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : order.status === 'DRAFT'
                      ? 'bg-slate-100 text-slate-600 border-slate-200'
                      : order.status === 'CANCELLED'
                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : hasActualProgress
                          ? 'bg-blue-50 text-blue-800 border-blue-200'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                  }`}>
                  {isCompletedOrder ? (
                    <>
                      <i className="ri-award-line text-emerald-600 text-sm" />
                      Production Grade: {costingGrade}
                    </>
                  ) : isOnHold ? (
                    'Status: Paused (ON HOLD)'
                  ) : order.status === 'DRAFT' ? (
                    'Status: Uninitialized (DRAFT)'
                  ) : order.status === 'CANCELLED' ? (
                    'Status: Order Cancelled'
                  ) : hasActualProgress ? (
                    'Status: In Progress'
                  ) : (
                    'Status: Planning Budget'
                  )}
                </span>
              </div>
            </div>

            {order.status === 'DRAFT' ? (
              <div className="py-10 text-center space-y-4">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-indigo-50 border border-indigo-200 text-[#4f46e5]">
                  <i className="ri-money-rupee-circle-line text-2xl" />
                </div>
                <div className="max-w-md mx-auto space-y-1">
                  <h4 className="text-base font-bold text-slate-800">Standard Costing Uninitialized (DRAFT)</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Financial entries and standard costs (material, labour, and overhead) will be calculated and initialized when the order is confirmed.
                  </p>
                </div>
                <button
                  onClick={handleConfirmOrder}
                  disabled={actionLoading}
                  className="h-9 px-4 rounded-xl bg-[#4f46e5] hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <i className="ri-checkbox-circle-line text-sm" />
                  Confirm Order to Initialize Costing
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
                    <thead className="bg-slate-50/80 text-slate-700 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3.5 text-left">Cost Component</th>
                        <th className="px-5 py-3.5 text-right">Standard Cost</th>
                        <th className="px-5 py-3.5 text-right">Actual Cost</th>
                        <th className="px-5 py-3.5 text-right">Variance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="px-5 py-3.5 font-medium text-slate-700">Material Cost</td>
                        <td className="px-5 py-3.5 text-right text-slate-800 font-medium">{formatINR(stdMatCost)}</td>
                        <td className="px-5 py-3.5 text-right font-medium text-indigo-600">
                          {isCompletedOrder ? formatINR(actMatCost) : actualPendingLabel}
                        </td>
                        <td className="px-5 py-3.5 text-right font-bold text-emerald-600">
                          {isCompletedOrder ? formatINR(actMatCost - stdMatCost) : '—'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-5 py-3.5 font-medium text-slate-700">Labour Cost</td>
                        <td className="px-5 py-3.5 text-right text-slate-800 font-medium">{formatINR(stdLabCost)}</td>
                        <td className="px-5 py-3.5 text-right font-bold text-indigo-600">
                          {isCompletedOrder ? formatINR(actLabCost) : actualPendingLabel}
                        </td>
                        <td className="px-5 py-3.5 text-right font-bold text-emerald-600">
                          {isCompletedOrder ? formatINR(actLabCost - stdLabCost) : '—'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-5 py-3.5 font-medium text-slate-700">Overhead Cost</td>
                        <td className="px-5 py-3.5 text-right text-slate-800 font-medium">{formatINR(stdOvhCost)}</td>
                        <td className="px-5 py-3.5 text-right font-medium text-indigo-600">
                          {isCompletedOrder ? formatINR(actOvhCost) : actualPendingLabel}
                        </td>
                        <td className="px-5 py-3.5 text-right text-slate-600">
                          {isCompletedOrder ? formatINR(actOvhCost - stdOvhCost) : '—'}
                        </td>
                      </tr>
                      <tr className="bg-slate-50/70 font-bold border-t border-slate-200">
                        <td className="px-5 py-4 text-slate-900">Total Cost</td>
                        <td className="px-5 py-4 text-right text-slate-900">{formatINR(stdTotalCost)}</td>
                        <td className="px-5 py-4 text-right text-indigo-700">
                          {isCompletedOrder ? formatINR(actTotalCost) : actualPendingLabel}
                        </td>
                        <td className="px-5 py-4 text-right text-emerald-600 font-bold">
                          {isCompletedOrder ? `${formatINR(costVariance)} (${costVariancePct}%)` : '—'}
                        </td>
                      </tr>
                      <tr className="bg-emerald-50/50 font-bold">
                        <td className="px-5 py-4 text-emerald-900">Per Unit Cost</td>
                        <td className="px-5 py-4 text-right text-emerald-900">{formatINR(stdPerUnit)}</td>
                        <td className="px-5 py-4 text-right text-emerald-900">
                          {isCompletedOrder ? formatINR(actPerUnit) : actualPendingLabel}
                        </td>
                        <td className="px-5 py-4 text-right text-emerald-700">
                          {isCompletedOrder ? formatINR(actPerUnit - stdPerUnit) : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className={`p-4 rounded-xl border flex items-center justify-between text-xs sm:text-sm ${isCompletedOrder
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : order.status === 'CANCELLED'
                    ? 'bg-rose-50 border-rose-200 text-rose-800'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                  }`}>
                  <div className="flex items-center gap-2">
                    <i className={`${isCompletedOrder ? 'ri-checkbox-circle-fill text-emerald-600' : order.status === 'CANCELLED' ? 'ri-close-circle-fill text-rose-600' : 'ri-information-line text-slate-500'} text-lg`} />
                    <span>
                      {isCompletedOrder ? (
                        <>Variance: <strong className="font-bold text-sm">{formatINR(costVariance)}</strong> ({costVariancePct}% · {budgetLabel(costVariance)})</>
                      ) : order.status === 'CANCELLED' ? (
                        <>Production order was <strong>CANCELLED</strong>. No material or labour costs will accrue.</>
                      ) : (
                        <>Standard Budget planned for <strong className="font-bold">{plannedOrderQty} units</strong> at {formatINR(stdPerUnit)}/unit. Actual cost and variance are calculated automatically when the order is marked complete.</>
                      )}
                    </span>
                  </div>
                  {isCompletedOrder && (
                    <span className="font-semibold bg-emerald-100 text-emerald-900 px-3 py-1 rounded-lg">{gradeForVariance(costVariancePct)} ({Math.abs(costVariancePct)}% variance)</span>
                  )}
                  {order.status === 'CANCELLED' && (
                    <span className="font-semibold bg-rose-100 text-rose-900 px-3 py-1 rounded-lg">Order Cancelled</span>
                  )}
                </div>
              </>
            )}
          </div>
        )}

      </div>

      {/* ── MODAL: MARK PRODUCTION COMPLETE CONFIRMATION ── */}
      {showCompleteConfirmModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 bg-emerald-600 text-white flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <i className="ri-check-double-line text-lg text-emerald-200" /> Complete Production Order {order.po_number}?
                </h3>
                <p className="text-xs text-emerald-100">Product: {order.item_name}</p>
              </div>
              <button
                onClick={() => setShowCompleteConfirmModal(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-emerald-100 hover:text-white hover:bg-emerald-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              {/* Strict Mode Verification Badge */}
              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-shield-check-fill text-emerald-600 text-xl shrink-0" />
                  <div>
                    <span className="font-bold text-emerald-900 block text-xs">Strict Mode Traceability Verified</span>
                    <span className="text-emerald-700 text-[11px]">All {totalStages} Stages COMPLETED &amp; Stage QC PASSED</span>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                  PASSED
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                  <span className="text-slate-500 block text-xs font-medium">Ordered</span>
                  <span className="text-base font-bold text-slate-800 mt-1 block">{plannedQty} Units</span>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <span className="text-emerald-700 block text-xs font-medium">Produced</span>
                  <span className="text-base font-bold text-emerald-700 mt-1 block">{producedToComplete} Units</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                  <span className="text-slate-500 block text-xs font-medium">Rejected</span>
                  <span className="text-base font-bold text-slate-700 mt-1 block">{rejectedToComplete} Units</span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Standard Cost:</span>
                  <span className="font-semibold text-slate-800">{formatINR(stdTotalCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Actual Cost:</span>
                  <span className="font-semibold text-indigo-600">{formatINR(closingActualCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Variance:</span>
                  <span className={`font-semibold ${liveVariance > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                    {liveVariancePct > 0 ? '+' : ''}{liveVariancePct}% ({formatINR(liveVariance)}) · {gradeForVariance(liveVariancePct)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2 text-emerald-700">
                  <span className="font-semibold">Full Traceability:</span>
                  <span className="font-bold text-emerald-700">100% Stage Genealogy Logged</span>
                </div>
              </div>

              {shortToComplete > 0 && (
                <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 leading-relaxed flex items-start gap-2">
                  <i className="ri-alert-fill text-amber-600 text-base mt-0.5 shrink-0" />
                  <span>
                    <strong>Completing with {producedToComplete} / {plannedQty} units.</strong> {shortToComplete} unit(s) will not be produced on this order
                    {limitingMaterial ? ` — ${limitingMaterial.itemName} is short by ${limitingMaterial.shortQty} ${limitingMaterial.unit}` : ''}.
                    {continuation
                      ? ` Issued material already covers ${continuation.qty} more — use "Produce Remaining" to run them through the stages instead.`
                      : ' To produce them here, close this, issue the remaining material and the stages will be raised again for the balance.'}
                  </span>
                </div>
              )}

              <div className="p-3.5 bg-emerald-50/80 rounded-xl border border-emerald-200 text-emerald-900 leading-relaxed font-medium flex items-start gap-2">
                <i className="ri-box-3-line text-emerald-700 text-base mt-0.5 shrink-0" />
                <span><strong>{producedToComplete} units</strong> will move to Finished Goods stock. Output batch will be registered and double-entry GL voucher will post automatically.</span>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCompleteConfirmModal(false)}
                className="h-8 px-4 rounded-xl text-slate-600 hover:bg-slate-200/60 text-xs font-medium cursor-pointer"
              >
                Cancel
              </button>
              {continuation && (
                <button
                  onClick={handleContinueProduction}
                  disabled={actionLoading}
                  className="h-9 px-4 rounded-xl border border-indigo-300 bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-play-list-add-line text-sm" />
                  Produce Remaining {continuation.qty}
                </button>
              )}
              <button
                onClick={handleConfirmProductionComplete}
                disabled={actionLoading}
                className="h-9 px-5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors shadow-md disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
              >
                {actionLoading ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-check-line text-sm" />}
                {shortToComplete > 0 ? `Complete ${producedToComplete} Units` : 'Confirm Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: PRINT TRAVELER ── */}
      {showPrintModal && (
        <POPrintModal
          order={order}
          materials={materials}
          workOrders={workOrders}
          onClose={() => setShowPrintModal(false)}
        />
      )}

      {/* ── MODAL: STANDARD COST DETAILS BREAKDOWN ── */}
      <StandardCostDetailsModal
        open={showStdCostModal}
        onClose={() => setShowStdCostModal(false)}
        order={order}
      />

      {/* ── MODAL: ACTUAL COST DETAILS BREAKDOWN ── */}
      <ActualCostDetailsModal
        open={showActualCostModal}
        onClose={() => setShowActualCostModal(false)}
        order={order}
      />

      {/* ── MODAL: ISSUE MATERIAL TO PRODUCTION ── */}
      {issueMaterialModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 bg-indigo-600 text-white flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2.5">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <i className="ri-share-forward-box-line text-lg text-indigo-200" /> Issue Material to Production
                  </h3>
                  {/* {allModalItemsIssued && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/30 text-emerald-100 border border-emerald-400/40 flex items-center gap-1 shadow-2xs">
                      <i className="ri-checkbox-circle-fill text-xs text-emerald-300" />
                      Materials Issued
                    </span>
                  )} */}
                </div>
                <p className="text-xs text-indigo-100 font-mono mt-0.5">
                  PRD: {order.po_number || order.order_number} | Product: {order.item_name || order.product_name}
                </p>
              </div>
              <button
                onClick={() => setIssueMaterialModalOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-indigo-100 hover:text-white hover:bg-indigo-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200/80">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Issued By (Operator)</label>
                  <select
                    value={issueOperator}
                    onChange={(e) => setIssueOperator(e.target.value)}
                    className="w-full h-8 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                  >
                    <option value="">Select operator</option>
                    {issueOperators.map((o: any) => (
                      <option key={o.id} value={o.id}>
                        {o.name}{o.employee_code ? ` (${o.employee_code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Warehouse</label>
                  <input
                    type="text"
                    readOnly
                    value={order.warehouse_name || '—'}
                    className="w-full h-8 px-2.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-700"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Issue Date/Time</label>
                  <input
                    type="datetime-local"
                    value={issueDateTime}
                    onChange={(e) => setIssueDateTime(e.target.value)}
                    className="w-full h-8 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                {allModalItemsIssued ? (
                  <div className="p-3 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between gap-3 text-emerald-900">
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 text-lg shrink-0" />
                      <div>
                        <span className="font-bold">Total Materials Issued:</span> All required materials for this production order have been fully issued to the shop floor.
                        <p className="text-[11px] text-emerald-700 font-normal mt-0.5">
                          No pending materials remaining to issue. All items are actively in production WIP.
                        </p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 shrink-0 flex items-center gap-1 shadow-2xs">
                      <i className="ri-check-double-line text-sm text-emerald-600" />
                      100% Issued
                    </span>
                  </div>
                ) : issueItems.some((it) => (Number(it.stockAvailable || 0) < (Number(it.requiredQty || 0) - Number(it.alreadyIssued || 0))) || Number(it.stockAvailable || 0) <= 0) && (
                  <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 text-amber-900">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-fill text-amber-600 text-base shrink-0" />
                      <div>
                        <span className="font-bold">Shortage Detected in {order.warehouse_name || 'Warehouse'}:</span> Only items with available stock will be issued.
                        <p className="text-[11px] text-amber-700 font-normal mt-0.5">
                          Remaining out-of-stock items will open the BOM Explosion popup card to generate production suggestions.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIssueMaterialModalOpen(false);
                        setShowBomExplosionModal(true);
                      }}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold shrink-0 cursor-pointer flex items-center gap-1.5 shadow-2xs"
                    >
                      <i className="ri-node-tree" /> BOM Explosion
                    </button>
                  </div>
                )}
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2.5">Item Name</th>
                      <th className="px-3 py-2.5 text-right">Total Required Qty</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-emerald-800 bg-emerald-50/60">Reserved Qty</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-indigo-800">Already Issued</th>
                      <th className="px-3 py-2.5 text-right w-28 font-semibold text-indigo-900 bg-indigo-50/70">Issue Now Qty</th>
                      <th className="px-3 py-2.5">Batch (if tracked)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {issueItems.map((item, idx) => {
                      const avail = Number(item.stockAvailable || 0);
                      const reserved = Number(item.reservedQty || 0);
                      const req = Number(item.requiredQty || 0);
                      const issued = Number(item.alreadyIssued || 0);
                      const remReq = Math.max(0, req - issued);
                      const isOutOfStock = avail <= 0 && reserved <= 0;

                      return (
                        <tr key={idx} className="hover:bg-slate-50/70">
                          <td className="px-3 py-2.5 font-semibold text-slate-800">
                            {item.itemName} <span className="text-slate-400 font-normal">({item.itemCode})</span>
                          </td>
                          {/* 1. Total Required Qty */}
                          <td className="px-3 py-2.5 text-right font-mono font-medium text-slate-700">
                            {req.toFixed(2)} {item.unit}
                          </td>
                          {/* 2. Reserved Qty */}
                          <td className="px-3 py-2.5 text-right bg-emerald-50/20">
                            {reserved >= req && req > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <i className="ri-checkbox-circle-line text-xs" />
                                {reserved.toFixed(2)} {item.unit}
                              </span>
                            ) : reserved > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                <i className="ri-alert-line text-xs" />
                                {reserved.toFixed(2)} {item.unit}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-normal text-slate-400">
                                0.00 {item.unit}
                              </span>
                            )}
                          </td>
                          {/* 3. Already Issued */}
                          <td className="px-3 py-2.5 text-right font-mono font-medium text-indigo-600">
                            {issued.toFixed(2)} {item.unit}
                          </td>
                          {/* 4. Issue Now Qty */}
                          <td className="px-3 py-2.5 text-right bg-indigo-50/20">
                            {remReq <= 0 ? (
                              <div className="flex flex-col items-end">
                                {/* <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                                  <i className="ri-checkbox-circle-fill text-xs text-emerald-600" />
                                  Materials Issued
                                </span> */}
                                <span className="text-[10px] text-emerald-600 font-medium mt-0.5">
                                  Issued ({issued.toFixed(2)} of {req.toFixed(2)} {item.unit})
                                </span>
                              </div>
                            ) : isOutOfStock ? (
                              <div className="flex flex-col items-end">
                                <input
                                  type="number"
                                  disabled
                                  value={0}
                                  className="w-20 h-8 px-2 text-right border border-slate-200 rounded-lg text-xs font-mono font-bold bg-slate-100 text-slate-400 cursor-not-allowed"
                                />
                                <span className="text-[10px] text-rose-500 font-medium mt-0.5">Out of Stock</span>
                              </div>
                            ) : (
                              <input
                                type="number"
                                min={0}
                                max={item.maxIssuableQty}
                                step="any"
                                value={item.issueNowQty}
                                onChange={(e) => {
                                  const val = Math.min(item.maxIssuableQty, Math.max(0, Number(e.target.value)));
                                  const updated = [...issueItems];
                                  updated[idx].issueNowQty = val;
                                  setIssueItems(updated);
                                }}
                                className="w-20 h-8 px-2 text-right border border-indigo-300 rounded-lg text-xs font-mono font-bold focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-indigo-50/40"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {item.isBatchTracked && item.availableBatches.length > 0 ? (
                              <select
                                value={item.selectedBatch}
                                onChange={(e) => {
                                  const updated = [...issueItems];
                                  updated[idx].selectedBatch = e.target.value;
                                  setIssueItems(updated);
                                }}
                                className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                              >
                                {item.availableBatches.map((b: any, bIdx: number) => (
                                  <option key={bIdx} value={b.batchNumber}>
                                    {b.batchNumber} (Avail: {b.availableQty})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-slate-500 font-mono text-[11px]">
                                {item.selectedBatch || 'Standard Stock'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-right justify-end gap-2">
              {/* <button
                type="button"
                onClick={() => {
                  setIssueMaterialModalOpen(false);
                  setShowBomExplosionModal(true);
                }}
                className="h-8 px-3.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-semibold flex items-center gap-1.5 cursor-pointer shadow-2xs"
              >
                <i className="ri-node-tree text-sm text-amber-600" />
                BOM Explosion (Check Shortages)
              </button> */}
              <div className="flex items-right gap-2">
                <button
                  onClick={() => setIssueMaterialModalOpen(false)}
                  className="h-8 px-4 rounded-xl text-slate-600 hover:bg-slate-200/60 text-xs font-medium cursor-pointer"
                >
                  {allModalItemsIssued ? 'Close' : 'Cancel'}
                </button>
                {allModalItemsIssued ? (
                  <button
                    type="button"
                    disabled
                    className="h-9 px-5 rounded-xl bg-emerald-600 text-white text-xs font-bold transition-colors shadow-xs flex items-center gap-1.5 opacity-90 cursor-default"
                  >
                    <i className="ri-checkbox-circle-fill text-sm" />
                    Materials Issued
                  </button>
                ) : (
                  <button
                    onClick={handleSaveMaterialIssue}
                    disabled={actionLoading}
                    className="h-9 px-5 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors shadow-md disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                  >
                    {actionLoading ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-share-forward-box-line text-sm" />}
                    Save Issue
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: START PRODUCTION ORDER ── */}
      {startOrderModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 bg-indigo-600 text-white flex items-center justify-between">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <i className="ri-play-line text-lg text-indigo-200" /> Start Production Order
              </h3>
              <button
                onClick={() => setStartOrderModalOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-indigo-100 hover:text-white hover:bg-indigo-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200/80">
                <div>
                  <span className="text-slate-500 block text-[11px]">PRD Number</span>
                  <span className="font-bold text-slate-800 font-mono">{order.po_number}</span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[11px]">Product</span>
                  <span className="font-bold text-slate-800">{order.item_name}</span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[11px]">Qty</span>
                  <span className="font-bold text-slate-800">{plannedQty} {order.unit || ''}</span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[11px]">Planned Dates</span>
                  <span className="font-bold text-slate-800">
                    {order.planned_start_date ? formatDateIST(order.planned_start_date) : '—'} → {order.planned_end_date ? formatDateIST(order.planned_end_date) : '—'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Actual Start Date</label>
                  <input
                    type="date"
                    value={startOrderForm.actualStartDate}
                    onChange={(e) => setStartOrderForm({ ...startOrderForm, actualStartDate: e.target.value })}
                    className="w-full h-8 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Shift</label>
                  <select
                    value={startOrderForm.shiftId}
                    onChange={(e) => setStartOrderForm({ ...startOrderForm, shiftId: e.target.value })}
                    className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                  >
                    <option value="">Select shift</option>
                    {startOrderMasters.shifts
                      .filter((sh: any) => {
                        const wh = sh.warehouse_id ?? sh.warehouseId;
                        return !wh || !startOrderForm.warehouseId || String(wh) === String(startOrderForm.warehouseId);
                      })
                      .map((sh: any) => (
                        <option key={sh.id} value={sh.id}>
                          {sh.name}{sh.start_time ? ` (${String(sh.start_time).slice(0, 5)}–${String(sh.end_time || '').slice(0, 5)})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Warehouse</label>
                  <select
                    value={startOrderForm.warehouseId}
                    onChange={(e) => setStartOrderForm({ ...startOrderForm, warehouseId: e.target.value, shiftId: '' })}
                    className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                  >
                    {/* Keeps the order's own warehouse selectable even before the list has loaded */}
                    {order.warehouse_id && !startOrderMasters.warehouses.some((w: any) => String(w.id) === String(order.warehouse_id)) && (
                      <option value={order.warehouse_id}>{order.warehouse_name || 'Current warehouse'}</option>
                    )}
                    {startOrderMasters.warehouses.map((w: any) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Assigned Supervisor (optional)</label>
                  <select
                    value={startOrderForm.supervisorId}
                    onChange={(e) => setStartOrderForm({ ...startOrderForm, supervisorId: e.target.value })}
                    className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                  >
                    <option value="">Not assigned</option>
                    {startOrderMasters.supervisors
                      .filter((o: any) => {
                        const wh = o.warehouse_id ?? o.warehouseId;
                        return !wh || !startOrderForm.warehouseId || String(wh) === String(startOrderForm.warehouseId);
                      })
                      .map((o: any) => (
                        <option key={o.id} value={o.id}>
                          {o.name}{o.skill ? ` — ${String(o.skill).replace(/_/g, ' ')}` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Notes</label>
                <textarea
                  value={startOrderForm.notes}
                  onChange={(e) => setStartOrderForm({ ...startOrderForm, notes: e.target.value })}
                  rows={3}
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setStartOrderModalOpen(false)}
                className="h-8 px-4 rounded-xl text-slate-600 hover:bg-slate-200/60 text-xs font-medium cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStartOrder}
                disabled={actionLoading}
                className="h-9 px-5 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors shadow-md disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
              >
                {actionLoading ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-play-line text-sm" />}
                Confirm Start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: BOM EXPLOSION FOR SHORTAGES ── */}
      {showBomExplosionModal && order?.bom_id && (
        <BOMExplosionModal
          isOpen={showBomExplosionModal}
          onClose={() => setShowBomExplosionModal(false)}
          bom={{
            id: order.bom_id,
            code: order.bom_code,
            version: order.bom_version_name,
            productName: order.item_name || '',
          }}
          initialQty={plannedQty}
          warehouseId={order.warehouse_id}
          warehouseName={order.warehouse_name}
          reservationShortages={displayReservations}
        />
      )}

      {/* ── MODAL: ORDER CONFIRMATION SUMMARY ── */}
      {confirmedSummary && (
        <OrderConfirmationSummaryModal
          summary={confirmedSummary}
          onClose={() => setConfirmedSummary(null)}
          onViewOrderDetails={() => {
            setConfirmedSummary(null);
            fetchDetails();
          }}
        />
      )}
    </AppLayout>
  );
}
